import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowLeft,
  AudioWaveform,
  Captions,
  Check,
  ChevronDown,
  CaseSensitive,
  Frame,
  Italic,
  Languages,
  LayoutTemplate,
  Loader2,
  MonitorPlay,
  MoreVertical,
  Palette,
  Plus,
  Settings,
  Sparkles,
  Trash2,
  Type,
  Upload,
} from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { appApiBaseUrl, appFetch, uploadFile } from '@/shared/api/app-client'
import type {
  ApiResponse,
  PaginatedResponse,
  Template,
  TemplateStyleVariant,
  AssetUploadResult,
} from '@/shared/api/app-types'
import { cn } from '@/shared/ui/cn'
import { captionLibraryStyles, TEMPLATE_BASE } from './home-canvas'
import { TemplatePreview } from '@/features/app-shell/components/template-preview'
import type { TemplatePreviewConfig, VariantPreviewConfig, OverlayChange } from '@/features/app-shell/components/template-preview'
import { buildCaptionRenderConfig, getCaptionStyleConfig } from '@/features/app-shell/components/caption-style-config'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { WorkspaceUpgradeDialog } from '@/features/billing/components/workspace-upgrade-dialog'
import { hasActivePaidMembership } from '@/features/billing/utils/membership-labels'

type BrandingTabKey =
  | 'caption'
  | 'audiogram'
  | 'text'
  | 'intro'
  | 'outro'
  | 'logo'
  | 'background'
  | 'settings'
  | 'fonts'

type TextEditorTab = 'animation' | 'style'

type VariantCardPreset = {
  key: string
  name: string
  subtitle: string
  sample: string
  accent?: string
}

const BRANDING_TABS: Array<{
  key: BrandingTabKey
  label: string
  icon: typeof Captions
}> = [
  { key: 'caption', label: '字幕', icon: Captions },
  { key: 'audiogram', label: '波形', icon: AudioWaveform },
  { key: 'text', label: '文字', icon: Type },
  { key: 'intro', label: '片头', icon: Sparkles },
  { key: 'outro', label: '片尾', icon: MonitorPlay },
  { key: 'logo', label: 'logo', icon: Palette },
  { key: 'background', label: '背景', icon: Frame },
  { key: 'settings', label: '设置', icon: Settings },
  { key: 'fonts', label: '字体', icon: Languages },
]

const AUDIOGRAM_PRESETS = [
  { key: 'spectrum', label: '频谱' },
  { key: 'bars', label: '条形' },
  { key: 'hills', label: '山峰' },
  { key: 'radial', label: '放射条' },
  { key: 'wave', label: '波形' },
]

const TEXT_PRESETS: VariantCardPreset[] = [
  { key: 'modern', name: '现代标题', subtitle: '简洁利落', sample: '现代标题' },
  { key: 'impact', name: '冲击标语', subtitle: '强势醒目', sample: '制造\n冲击' },
  { key: 'sliced', name: '切片文字', subtitle: '锋利前卫', sample: '切片', accent: 'text-rose-400' },
  { key: 'minimal', name: '极简黑白', subtitle: '纯净克制', sample: '少\n即\n是多' },
  { key: 'highlight', name: '高亮框', subtitle: '醒目突出', sample: '重点\n文字', accent: 'bg-indigo-600' },
  { key: 'gradient', name: '渐变弹出', subtitle: '动感现代', sample: '渐变样式', accent: 'text-orange-400' },
  { key: 'neon', name: '霓虹未来', subtitle: '电感强烈', sample: '霓虹\n未来', accent: 'text-cyan-300' },
  { key: 'avant', name: '前卫风格', subtitle: '时尚表达', sample: '前卫\n设计' },
  { key: 'glass', name: '玻璃拟态', subtitle: '通透现代', sample: '玻璃\n效果' },
]

const FONT_LIBRARY = [
  { family: 'Komika Axis', useFor: '夸张字幕', sample: '大胆表达' },
  { family: 'Bebas Neue', useFor: '强钩子标题', sample: '放大声量' },
  { family: 'Space Grotesk', useFor: '现代解说', sample: '清晰系统' },
  { family: 'DM Sans', useFor: '产品文案', sample: '多平台流程' },
  { family: 'Playfair Display', useFor: '高级标题卡', sample: '优雅发布' },
  { family: 'JetBrains Mono', useFor: '科技品牌感', sample: '持续迭代()' },
]

const TEXT_EDITOR_FONT_OPTIONS = [
  'Inter',
  'Komika Axis',
  'Bebas Neue',
  'Space Grotesk',
  'Playfair Display',
  'DM Sans',
  'JetBrains Mono',
  'boldslay',
  'Anson',
  'Avenir Next',
  'Bangers',
  'Barriecito',
  'Bungee',
  'Changa',
  'Epilogue',
  'Fredoka',
  'Helvetica Neue',
]
const TEXT_ANIMATION_OPTIONS = [
  { key: 'none', name: '无', note: '不添加动效' },
  { key: 'fade', name: '淡入', note: '透明度渐显' },
  { key: 'scale', name: '缩放', note: '从中心放大' },
  { key: 'bounce', name: '弹跳', note: '带回弹出现' },
  { key: 'flip', name: '翻转', note: '沿 X 轴翻入' },
  { key: 'zoom', name: '拉近', note: '缩放并带模糊' },
  { key: 'slide', name: '上滑', note: '从底部进入' },
  { key: 'snap', name: '卡点', note: '快速旋转归位' },
  { key: 'glitch', name: '故障', note: '数字闪烁感' },
  { key: 'swipe', name: '擦入', note: '遮罩显现' },
  { key: 'float', name: '漂浮', note: '轻柔飘入' },
  { key: 'spin', name: '旋转', note: '原地转入' },
  { key: 'slide-d', name: '下落', note: '从顶部落下' },
  { key: 'slide-l', name: '左推', note: '从左侧推进' },
  { key: 'diagonal', name: '斜向', note: '沿角落路径移动' },
  { key: 'wobble', name: '晃动', note: '轻微摇摆后稳定' },
  { key: 'flip-y', name: 'Y 翻转', note: '沿 Y 轴翻入' },
  { key: 'pulse', name: '脉冲', note: '跳动后回稳' },
  { key: 'drop', name: '落入', note: '坠入到位' },
  { key: 'squeeze', name: '挤压', note: '压缩后展开' },
  { key: 'roll', name: '滚入', note: '从侧边滚入' },
  { key: 'swing', name: '摆动', note: '钟摆式回稳' },
  { key: 'expand', name: '扩张', note: '从中心放大' },
  { key: 'expand-h', name: '横向展开', note: '水平方向铺开' },
  { key: 'twist', name: '扭转', note: '扭动进入视野' },
  { key: 'blur', name: '清晰化', note: '由模糊变清晰' },
  { key: 'spiral', name: '螺旋', note: '旋转并放大进入' },
  { key: 'shake', name: '抖动', note: '震颤后停止' },
  { key: 'curtain', name: '幕布', note: '通过遮罩揭示' },
  { key: 'fold', name: '折叠', note: '折开出现' },
  { key: 'zigzag', name: '折线', note: '快速交替位移' },
  { key: 'elastic', name: '弹性', note: '拉伸后回稳' },
  { key: 'slingshot', name: '弹弓', note: '超出后回弹' },
  { key: 'rotate', name: '旋入', note: '简单旋转进入' },
  { key: 'skew', name: '倾斜', note: '斜切后归位' },
  { key: 'peek', name: '探出', note: '从下方探出' },
  { key: 'vortex', name: '涡旋', note: '从纵深旋入' },
]

function resolveUrl(url: string | null): string | null {
  if (!url) return null
  if (url.startsWith('/uploads/')) {
    return `${appApiBaseUrl}${url}`
  }
  return url
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function mergeTemplateConfig(
  base: Record<string, unknown>,
  incoming: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!incoming) return { ...base }

  const merged: Record<string, unknown> = { ...base, ...incoming }
  const nestedKeys = ['captions', 'audiogram', 'background', 'intro', 'outro'] as const

  for (const key of nestedKeys) {
    const baseValue = asRecord(base[key])
    const incomingValue = asRecord(incoming[key])
    if (baseValue || incomingValue) {
      merged[key] = {
        ...(baseValue ?? {}),
        ...(incomingValue ?? {}),
      }
    }
  }

  if (Array.isArray(incoming.logos)) {
    merged.logos = incoming.logos
  }

  return merged
}

function buildTextPresetDefaults(presetKey: string) {
  const preset = TEXT_PRESETS.find((item) => item.key === presetKey)
  return {
    enabled: true,
    content: preset?.sample ?? preset?.name ?? 'Text',
    fontFamily: 'Inter',
    align: 'center' as const,
    color:
      presetKey === 'sliced' ? '#FF5C7A'
        : presetKey === 'gradient' ? '#FF9800'
          : presetKey === 'neon' ? '#38DFFF'
            : '#FFFFFF',
    accentColor: presetKey === 'highlight' ? '#5B4BFF' : '#FFD93D',
    fontWeight: presetKey === 'minimal' ? 700 : 800,
    italic: presetKey === 'gradient',
    uppercase: presetKey !== 'minimal',
    fontSize: presetKey === 'impact' ? 32 : presetKey === 'sliced' ? 34 : 28,
    lineHeight: presetKey === 'minimal' ? 1.08 : 0.98,
    letterSpacing: presetKey === 'playfair' ? 0 : -0.03,
    rotation: presetKey === 'sliced' ? -12 : 0,
    animation: presetKey === 'sliced' ? 'scale' : 'fade',
  }
}

/**
 * 切换到不同 Text 预设时，用新预设的默认参数重新初始化 text 配置。
 * 只保留结构性字段（enabled / 位置 x,y,width,height），丢弃上一个预设的样式覆盖，
 * 这样选 B 不会残留 A 的编辑值。
 */
function resetTextPreset(presetKey: string, existing: Record<string, unknown> | undefined): Record<string, unknown> {
  const defaults = buildTextPresetDefaults(presetKey)
  return {
    ...defaults,
    enabled: existing?.enabled !== false,
    ...(typeof existing?.x === 'number' ? { x: existing.x } : {}),
    ...(typeof existing?.y === 'number' ? { y: existing.y } : {}),
    ...(typeof existing?.width === 'number' ? { width: existing.width } : {}),
    ...(typeof existing?.height === 'number' ? { height: existing.height } : {}),
  }
}

function normalizeTextAnimationSample(animationKey: string) {
  switch (animationKey) {
    case 'slide-d':
      return 'slide'
    case 'slide-l':
    case 'diagonal':
      return 'swipe'
    case 'flip-y':
      return 'flip'
    case 'drop':
    case 'peek':
      return 'slide'
    case 'squeeze':
    case 'expand':
    case 'expand-h':
    case 'elastic':
    case 'slingshot':
    case 'vortex':
      return 'scale'
    case 'roll':
    case 'twist':
    case 'spiral':
    case 'rotate':
      return 'spin'
    case 'swing':
    case 'wobble':
    case 'shake':
    case 'zigzag':
      return 'bounce'
    case 'blur':
      return 'zoom'
    case 'curtain':
      return 'swipe'
    case 'skew':
      return 'snap'
    default:
      return animationKey
  }
}

function resolveTextEditorFontStack(fontFamily: string | undefined) {
  switch ((fontFamily ?? '').trim()) {
    case 'Komika Axis':
      return '"Komika Axis", "Arial Black", Impact, sans-serif'
    case 'Bebas Neue':
      return '"Bebas Neue", "Arial Narrow", Impact, sans-serif'
    case 'Space Grotesk':
      return '"Space Grotesk", "Trebuchet MS", "Segoe UI", sans-serif'
    case 'Playfair Display':
      return '"Playfair Display", Georgia, "Times New Roman", serif'
    case 'DM Sans':
      return '"DM Sans", Inter, "Segoe UI", sans-serif'
    case 'JetBrains Mono':
      return '"JetBrains Mono", "SFMono-Regular", Consolas, monospace'
    case 'boldslay':
    case 'Bangers':
      return `"${fontFamily}", Impact, "Arial Black", sans-serif`
    case 'Avenir Next':
    case 'Helvetica Neue':
      return `"${fontFamily}", "Segoe UI", Arial, sans-serif`
    case 'Barriecito':
    case 'Fredoka':
    case 'Bungee':
    case 'Changa':
    case 'Anson':
    case 'Epilogue':
      return `"${fontFamily}", Inter, "Segoe UI", sans-serif`
    case 'Inter':
      return 'Inter, "Segoe UI", sans-serif'
    default:
      return fontFamily ? `"${fontFamily}", sans-serif` : 'Inter, "Segoe UI", sans-serif'
  }
}

/**
 * 把选中的字幕样式配置物化成可保存到 localConfig.captions 的字段。
 * 这样保存时 captions 自包含完整渲染参数，不依赖运行时按 key 再解析。
 * 保留已有 overrides（用户手动改过的值优先）—— 用于编辑器内字段微调后重新物化。
 */
function materializeCaptionStyle(styleKey: string, existing: Record<string, unknown> | undefined): Record<string, unknown> {
  const base = getCaptionStyleConfig(styleKey)
  const rendered = buildCaptionRenderConfig(base, existing)
  return {
    ...(existing ?? {}),
    enabled: existing?.enabled !== false,
    fontFamily: rendered.fontFamily,
    fontSize: rendered.fontSize,
    color: rendered.color,
    fontWeight: rendered.fontWeight,
    strokeColor: rendered.strokeColor,
    strokeWidth: rendered.strokeWidth,
    textTransform: rendered.textTransform,
    letterSpacing: rendered.letterSpacing,
    lineHeight: rendered.lineHeight,
    textAlign: rendered.textAlign,
    italic: rendered.italic,
    maxWordsPerLine: rendered.maxWordsPerLine,
    highlightMode: rendered.highlightMode,
    ...(rendered.highlightColor ? { highlightColor: rendered.highlightColor } : {}),
    ...(rendered.wordAnimation ? { wordAnimation: rendered.wordAnimation } : {}),
  }
}

/**
 * 切换到不同字幕样式时，用新样式的默认参数重新物化 captions。
 * 只保留结构性字段（enabled / 位置 x,y,width），丢弃上一个样式的样式覆盖，
 * 这样选 B 不会残留 A 的编辑值。
 */
function resetCaptionStyle(styleKey: string, existing: Record<string, unknown> | undefined): Record<string, unknown> {
  const base = getCaptionStyleConfig(styleKey)
  const rendered = buildCaptionRenderConfig(base, undefined)
  return {
    // 只保留结构性字段
    enabled: existing?.enabled !== false,
    ...(typeof existing?.x === 'number' ? { x: existing.x } : {}),
    ...(typeof existing?.y === 'number' ? { y: existing.y } : {}),
    ...(typeof existing?.width === 'number' ? { width: existing.width } : {}),
    // 其余全部用新样式的默认值
    fontFamily: rendered.fontFamily,
    fontSize: rendered.fontSize,
    color: rendered.color,
    fontWeight: rendered.fontWeight,
    strokeColor: rendered.strokeColor,
    strokeWidth: rendered.strokeWidth,
    textTransform: rendered.textTransform,
    letterSpacing: rendered.letterSpacing,
    lineHeight: rendered.lineHeight,
    textAlign: rendered.textAlign,
    italic: rendered.italic,
    maxWordsPerLine: rendered.maxWordsPerLine,
    highlightMode: rendered.highlightMode,
    textShadow: rendered.textShadow,
    ...(rendered.highlightColor ? { highlightColor: rendered.highlightColor } : {}),
    ...(rendered.wordAnimation ? { wordAnimation: rendered.wordAnimation } : {}),
  }
}

export function BrandingCanvas() {
  const membership = useAuthSessionStore((state) => state.session?.membership)
  const [templates, setTemplates] = useState<Template[]>([])
  const [loadingList, setLoadingList] = useState(true)
  const [listError, setListError] = useState<string | null>(null)

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detail, setDetail] = useState<Template | null>(null)
  const [variants, setVariants] = useState<TemplateStyleVariant[]>([])
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  const [activeTab, setActiveTab] = useState<BrandingTabKey>('caption')
  const [isCaptionEditorOpen, setIsCaptionEditorOpen] = useState(false)
  const [isTextEditorOpen, setIsTextEditorOpen] = useState(false)
  const [activeTextEditorTab, setActiveTextEditorTab] = useState<TextEditorTab>('style')

  // 本地配置状态（手动保存模式）
  const [localConfig, setLocalConfig] = useState<Record<string, unknown>>({})
  const [localVariantConfigs, setLocalVariantConfigs] = useState<Record<number, Record<string, unknown>>>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // 模板管理：新建 / 重命名 / 删除
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createAspect, setCreateAspect] = useState<'9:16' | '1:1' | '16:9'>('9:16')
  const [creating, setCreating] = useState(false)
  const [renameTarget, setRenameTarget] = useState<Template | null>(null)
  const [renameName, setRenameName] = useState('')
  const [renaming, setRenaming] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false)
  const hasBrandTemplateSubscription = hasActivePaidMembership(membership?.planCode, membership?.status)

  // 加载上传的自定义字体：用 CSS Font Loading API 注册 @font-face
  // 这样在字幕/文字编辑器选择上传字体后，预览才能真实渲染该字体
  useEffect(() => {
    const fonts = (localConfig.fonts as Array<{ fontFamily?: string; publicUrl?: string; fileName?: string }>) ?? []
    const loadedKeys = new Set<string>()
    const registeredFaces: FontFace[] = []
    let cancelled = false

    for (const font of fonts) {
      const family = font.fontFamily ?? font.fileName ?? ''
      const url = font.publicUrl ? resolveUrl(font.publicUrl) : null
      if (!family || !url || loadedKeys.has(family)) continue
      loadedKeys.add(family)

      const fontFace = new FontFace(family, `url("${url}")`)
      registeredFaces.push(fontFace)
      fontFace.load().then(() => {
        if (!cancelled) {
          document.fonts.add(fontFace)
        }
      }).catch(() => {
        // 字体加载失败时静默，fallback 到 sans-serif
      })
    }

    return () => {
      cancelled = true
      for (const ff of registeredFaces) {
        document.fonts.delete(ff)
      }
    }
  }, [localConfig.fonts])

  useEffect(() => {
    let cancelled = false
    setLoadingList(true)
    setListError(null)

    const params = new URLSearchParams({
      templateScope: 'brand',
      page: '1',
      pageSize: '50',
    })

    void appFetch<ApiResponse<PaginatedResponse<Template>>>(
      `/api/templates?${params.toString()}`,
    )
      .then((res) => {
        if (cancelled) return
        setTemplates(res.data.items)
        if (res.data.items.length > 0 && selectedId === null) {
          setSelectedId(res.data.items[0]?.id ?? null)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setListError(err instanceof Error ? err.message : '加载模板失败')
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedId === null) {
      setDetail(null)
      setVariants([])
      return
    }

    let cancelled = false
    setLoadingDetail(true)
    setDetailError(null)
    setIsCaptionEditorOpen(false)
    setIsTextEditorOpen(false)
    setActiveTextEditorTab('style')
    setActiveTab('caption')

    Promise.all([
      appFetch<ApiResponse<Template>>(`/api/templates/${selectedId}`),
      appFetch<ApiResponse<{ items: TemplateStyleVariant[]; total: number }>>(
        `/api/templates/${selectedId}/style-variants`,
      ),
    ])
      .then(([tplRes, variantRes]) => {
        if (cancelled) return
        const initialConfig = mergeTemplateConfig({}, tplRes.data.config ?? {})
        // 如果已有 captionStyleKey 但 captions 是旧数据（未物化），补齐完整渲染参数
        const styleKey = initialConfig.captionStyleKey as string | undefined
        if (styleKey) {
          const prevCaptions = (initialConfig.captions as Record<string, unknown> | undefined) ?? {}
          initialConfig.captions = materializeCaptionStyle(styleKey, prevCaptions)
        }
        setDetail({ ...tplRes.data, config: initialConfig })
        setVariants(variantRes.data.items)
        // 初始化本地配置副本
        setLocalConfig(initialConfig)
        const vConfigs: Record<number, Record<string, unknown>> = {}
        for (const v of variantRes.data.items) {
          vConfigs[v.id] = { ...v.config }
        }
        setLocalVariantConfigs(vConfigs)
      })
      .catch((err) => {
        if (!cancelled) {
          setDetailError(err instanceof Error ? err.message : '加载模板详情失败')
          setDetail(null)
          setVariants([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedId])

  const summary = useMemo(
    () => buildBrandingVariantSummary(localConfig, detail, activeTab),
    [localConfig, detail, activeTab],
  )


  // 更新本地模板配置
  const updateLocalConfig = useCallback((updates: Record<string, unknown>) => {
    setLocalConfig((prev) => ({ ...prev, ...updates }))
  }, [])

  /**
   * 接收右侧预览的 overlay 拖拽/resize 结果，写回 localConfig。
   * 坐标与尺寸统一为百分比（0-100），保存时随模板配置一起持久化。
   */
  const handleOverlayChange = useCallback((change: OverlayChange) => {
    setLocalConfig((prev) => {
      if (change.type === 'caption') {
        const prevCaptions = (prev.captions as { enabled?: boolean; x?: number; y?: number; width?: number } | undefined) ?? { enabled: true }
        return {
          ...prev,
          captions: {
            ...prevCaptions,
            ...(typeof change.x === 'number' ? { x: change.x } : {}),
            ...(typeof change.y === 'number' ? { y: change.y } : {}),
            ...(typeof change.width === 'number' ? { width: change.width } : {}),
          },
        }
      }
      if (change.type === 'logo') {
        const prevLogos = (prev.logos as Array<Record<string, unknown>>) ?? []
        const index = typeof change.id === 'number' ? change.id : -1
        const nextLogos = prevLogos.map((logo, i) => (i === index ? {
          ...logo,
          ...(typeof change.x === 'number' ? { x: change.x } : {}),
          ...(typeof change.y === 'number' ? { y: change.y } : {}),
          ...(typeof change.width === 'number' ? { width: change.width } : {}),
          ...(typeof change.height === 'number' ? { height: change.height } : {}),
        } : logo))
        return { ...prev, logos: nextLogos }
      }
      if (change.type === 'audiogram') {
        const prevAudiogram = (prev.audiogram as { enabled?: boolean; x?: number; y?: number; width?: number; height?: number } | undefined) ?? { enabled: true }
        return {
          ...prev,
          audiogram: {
            ...prevAudiogram,
            ...(typeof change.x === 'number' ? { x: change.x } : {}),
            ...(typeof change.y === 'number' ? { y: change.y } : {}),
            ...(typeof change.width === 'number' ? { width: change.width } : {}),
            ...(typeof change.height === 'number' ? { height: change.height } : {}),
          },
        }
      }
      if (change.type === 'text') {
        const prevText = (prev.text as { enabled?: boolean; x?: number; y?: number; width?: number; height?: number } | undefined) ?? { enabled: true }
        return {
          ...prev,
          text: {
            ...prevText,
            ...(typeof change.x === 'number' ? { x: change.x } : {}),
            ...(typeof change.y === 'number' ? { y: change.y } : {}),
            ...(typeof change.width === 'number' ? { width: change.width } : {}),
            ...(typeof change.height === 'number' ? { height: change.height } : {}),
          },
        }
      }
      return prev
    })
  }, [])

  // dirty 检测
  const isConfigDirty = useMemo(() => {
    if (!detail) return false
    return JSON.stringify(localConfig) !== JSON.stringify(detail.config ?? {})
  }, [localConfig, detail])

  const isVariantDirty = useMemo(() => {
    for (const v of variants) {
      const local = localVariantConfigs[v.id]
      if (local && JSON.stringify(local) !== JSON.stringify(v.config)) return true
    }
    return false
  }, [localVariantConfigs, variants])

  const dirty = isConfigDirty || isVariantDirty

  // 保存
  const handleSave = useCallback(async () => {
    if (!selectedId || !detail) return
    if (!hasBrandTemplateSubscription) {
      setUpgradeDialogOpen(true)
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      // 保存模板配置
      await appFetch<ApiResponse<Template>>(`/api/templates/${selectedId}`, {
        method: 'PATCH',
        body: JSON.stringify({ config: localConfig }),
      })
      // 保存变体配置
      for (const [vidStr, config] of Object.entries(localVariantConfigs)) {
        const vid = Number(vidStr)
        const original = variants.find((v) => v.id === vid)
        if (original && JSON.stringify(config) !== JSON.stringify(original.config)) {
          await appFetch<ApiResponse<TemplateStyleVariant>>(
            `/api/templates/${selectedId}/style-variants/${vid}`,
            { method: 'PATCH', body: JSON.stringify({ config }) },
          )
        }
      }
      // 保存后重新拉取详情，同步 localConfig/localVariantConfigs → dirty 清零
      const [savedTplRes, savedVariantRes] = await Promise.all([
        appFetch<ApiResponse<Template>>(`/api/templates/${selectedId}`),
        appFetch<ApiResponse<{ items: TemplateStyleVariant[]; total: number }>>(`/api/templates/${selectedId}/style-variants`),
      ])
      const mergedSavedConfig = mergeTemplateConfig(localConfig, savedTplRes.data.config ?? {})
      setDetail({ ...savedTplRes.data, config: mergedSavedConfig })
      setVariants(savedVariantRes.data.items)
      setLocalConfig(mergedSavedConfig)
      const freshVConfigs: Record<number, Record<string, unknown>> = {}
      for (const v of savedVariantRes.data.items) {
        freshVConfigs[v.id] = { ...v.config }
      }
      setLocalVariantConfigs(freshVConfigs)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }, [selectedId, detail, hasBrandTemplateSubscription, localConfig, localVariantConfigs, variants])

  // 刷新模板列表
  const refreshTemplateList = useCallback(async () => {
    const params = new URLSearchParams({ templateScope: 'brand', page: '1', pageSize: '50' })
    const res = await appFetch<ApiResponse<PaginatedResponse<Template>>>(`/api/templates?${params.toString()}`)
    setTemplates(res.data.items)
    return res.data.items
  }, [])

  // 创建模板
  const handleCreate = useCallback(async () => {
    if (!hasBrandTemplateSubscription) {
      setCreateDialogOpen(false)
      setUpgradeDialogOpen(true)
      return
    }
    const name = createName.trim()
    if (!name) return
    setCreating(true)
    try {
      const res = await appFetch<ApiResponse<Template>>('/api/templates', {
        method: 'POST',
        body: JSON.stringify({ templateName: name, aspectRatio: createAspect }),
      })
      const items = await refreshTemplateList()
      setSelectedId(res.data.id)
      setCreateDialogOpen(false)
      setCreateName('')
      setCreateAspect('9:16')
      void items
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : '创建失败')
    } finally {
      setCreating(false)
    }
  }, [createName, createAspect, hasBrandTemplateSubscription, refreshTemplateList])

  // 重命名模板
  const handleRename = useCallback(async () => {
    if (!renameTarget) return
    const name = renameName.trim()
    if (!name) return
    setRenaming(true)
    try {
      await appFetch<ApiResponse<Template>>(`/api/templates/${renameTarget.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ templateName: name }),
      })
      const items = await refreshTemplateList()
      // 如果当前选中的就是被重命名的模板，更新 detail
      if (selectedId === renameTarget.id && detail) {
        setDetail({ ...detail, templateName: name })
      }
      setRenameTarget(null)
      void items
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : '重命名失败')
    } finally {
      setRenaming(false)
    }
  }, [renameTarget, renameName, refreshTemplateList, selectedId, detail])

  // 删除模板
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await appFetch<ApiResponse<null>>(`/api/templates/${deleteTarget.id}`, { method: 'DELETE' })
      const items = await refreshTemplateList()
      // 如果当前选中的就是被删除的模板，切到第一个
      if (selectedId === deleteTarget.id) {
        setSelectedId(items[0]?.id ?? null)
      }
      setDeleteTarget(null)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : '删除失败')
    } finally {
      setDeleting(false)
    }
  }, [deleteTarget, refreshTemplateList, selectedId])

  // 预览配置
  const previewConfig = useMemo<TemplatePreviewConfig>(() => ({
    aspectRatio: (localConfig.aspectRatio as string) ?? detail?.aspectRatio ?? '9:16',
    resolution: (localConfig.resolution as string) ?? `${detail?.width ?? 1080}x${detail?.height ?? 1920}`,
    backgroundColor: (localConfig.backgroundColor as string) ?? '#000000',
    background: (localConfig.background as TemplatePreviewConfig['background']) ?? null,
    backgroundEnabled: (localConfig.backgroundEnabled as boolean | undefined) ?? true,
    logos: (localConfig.logos as TemplatePreviewConfig['logos']) ?? [],
    logoEnabled: (localConfig.logoEnabled as boolean | undefined) ?? true,
    intro: (localConfig.intro as TemplatePreviewConfig['intro']) ?? null,
    outro: (localConfig.outro as TemplatePreviewConfig['outro']) ?? null,
    captions: (localConfig.captions as { enabled?: boolean; x?: number; y?: number; width?: number }) ?? { enabled: true },
    captionStyleKey: (localConfig.captionStyleKey as string) ?? undefined,
    audiogramPreset: (localConfig.audiogramPreset as string) ?? undefined,
    audiogram: (localConfig.audiogram as TemplatePreviewConfig['audiogram']) ?? undefined,
    textPreset: (localConfig.textPreset as string) ?? undefined,
    text: (localConfig.text as TemplatePreviewConfig['text']) ?? undefined,
    logoLayout: (localConfig.logoLayout as string) ?? undefined,
    backgroundPreset: (localConfig.backgroundPreset as string) ?? undefined,
  }), [localConfig, detail])

  // 字幕预览配置：由 selectedStyleKey 派生基础样式，再被 localConfig.captions 的覆写字段覆盖。
  // 这样选样式网格、深编辑器、右侧预览共用同一份本地编辑态，不再依赖 selectedVariantId。
  const previewVariantConfig = useMemo<VariantPreviewConfig | null>(() => {
    if (!detail) return null
    const styleKey = (localConfig.captionStyleKey as string) ?? 'blue'
    const base = getCaptionStyleConfig(styleKey)
    const captions = (localConfig.captions as Record<string, unknown> | undefined) ?? {}
    return buildCaptionRenderConfig(base, captions)
  }, [detail, localConfig.captionStyleKey, localConfig.captions])

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-surface-0">
      <header className="sticky top-0 z-10 flex shrink-0 items-center gap-2.5 px-6 py-1.5">
        <Link
          to="/app"
          className="rounded-xl p-2.5 text-[#878787] transition-colors hover:bg-[#222] hover:text-white"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div className="ml-4 flex items-center gap-3 text-neutral-100">
          <span className="text-base font-semibold text-neutral-100">品牌模板</span>
          <span className="inline-flex shrink-0 items-center rounded-full border border-hairline bg-white/4 px-1.5 py-0 text-[10px] font-medium uppercase tracking-wide text-neutral-300 backdrop-blur-xs">
            专业版
          </span>
          {detail ? (
            <>
              <span className="text-sm text-neutral-500">/</span>
              <span className="text-sm text-neutral-300">{detail.templateName}</span>
            </>
          ) : null}
        </div>
        <div className="grow" />
        {detail ? (
          <div className="flex items-center gap-2">
            {dirty ? <span className="text-xs text-amber-400">● 未保存</span> : null}
            {saveError ? <span className="text-xs text-red-400">{saveError}</span> : null}
            <Button
              size="sm"
              className="h-7 gap-1.5 text-xs"
              disabled={!dirty || saving}
              onClick={handleSave}
            >
              {saving ? <Loader2 className="size-3.5 animate-spin" /> : null}
              {saving ? '保存中…' : '保存'}
            </Button>
          </div>
        ) : null}
      </header>

      <div className="grid min-h-0 flex-1 gap-3 px-3 pb-3 xl:grid-cols-[224px_minmax(0,1fr)_340px] 2xl:grid-cols-[224px_minmax(0,1fr)_380px]">
        <aside className="hidden min-h-0 flex-col overflow-hidden rounded-2xl border border-hairline bg-surface-1 xl:flex">
          <div className="flex h-12 shrink-0 items-center justify-between border-b border-hairline px-4">
            <span className="text-sm font-semibold text-neutral-100">模板列表</span>
            <button
              type="button"
              onClick={() => {
                if (!hasBrandTemplateSubscription) {
                  setUpgradeDialogOpen(true)
                  return
                }
                setCreateName('')
                setCreateAspect('9:16')
                setCreateDialogOpen(true)
              }}
              className="inline-flex h-8 items-center gap-1 rounded-full border border-hairline bg-neutral-800 px-3 text-xs font-medium text-neutral-100 transition-colors hover:bg-neutral-700"
            >
              <Plus className="size-3.5" />
              新建
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {loadingList ? (
              <div className="flex h-32 items-center justify-center text-xs text-neutral-500">
                加载中...
              </div>
            ) : listError ? (
              <div className="flex h-32 items-center justify-center text-xs text-neutral-500">
                {listError}
              </div>
            ) : templates.length === 0 ? (
              <div className="flex h-32 flex-col items-center justify-center gap-2 text-xs text-neutral-500">
                <LayoutTemplate className="size-6" />
                暂无品牌模板
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {templates.map((tpl) => {
                  const isActive = selectedId === tpl.id
                  const isSystem = tpl.visibility === 'system'

                  return (
                    <div
                      key={tpl.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedId(tpl.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(tpl.id) } }}
                      className={cn(
                        'group relative flex h-[120px] cursor-pointer flex-col rounded-xl text-left ring-2 transition-all hover:shadow-md',
                        isActive ? 'ring-sky-600' : 'ring-transparent hover:ring-neutral-700',
                      )}
                    >
                      <div className="relative h-[90px] overflow-hidden rounded-t-xl bg-neutral-950">
                        {tpl.coverUrl ? (
                          <img
                            src={resolveUrl(tpl.coverUrl) ?? ''}
                            alt={tpl.templateName}
                            className="size-full object-cover"
                          />
                        ) : (
                          <TemplatePreviewPlaceholder template={tpl} />
                        )}

                        {tpl.visibility === 'system' ? (
                          <span className="absolute left-1.5 top-1.5 z-10 rounded-md bg-sky-500/15 px-1.5 py-[2px] text-[9px] font-semibold uppercase tracking-wider text-sky-300 ring-1 ring-inset ring-sky-500/30">
                            {tpl.isDefault ? '默认' : '示例'}
                          </span>
                        ) : null}

                        {/* 非系统模板：hover 显示更多操作按钮 */}
                        {!isSystem ? (
                          <TemplateCardMenu
                            onRename={() => { setRenameTarget(tpl); setRenameName(tpl.templateName) }}
                            onDelete={() => setDeleteTarget(tpl)}
                          />
                        ) : null}

                        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/65 px-2 py-1 backdrop-blur-sm">
                          <AspectGlyph aspectRatio={tpl.aspectRatio} />
                          <span className="text-[10px] font-semibold tracking-wide text-white/85">
                            {Math.min(tpl.width ?? 1080, tpl.height ?? 1920)}
                          </span>
                          <span className="rounded bg-white px-1 py-px text-[10px] font-bold leading-none text-black">
                            CC
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-1 items-center justify-center rounded-b-xl bg-neutral-700 px-3">
                        <span className="truncate text-xs font-medium text-neutral-200">
                          {tpl.templateName}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </aside>

        <section className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-hairline bg-surface-1">
          <div className="flex h-12 shrink-0 items-center justify-between border-b border-hairline px-4">
            <span className="text-sm font-semibold text-neutral-100">配置</span>
            {activeTab === 'caption' && !isCaptionEditorOpen ? (
              <button
                type="button"
                onClick={() => setIsCaptionEditorOpen(true)}
                className="inline-flex h-8 items-center rounded-full border border-hairline bg-neutral-800 px-3 text-xs font-medium text-neutral-100 transition-colors hover:bg-neutral-700"
              >
                编辑字幕样式
              </button>
            ) : null}
          </div>

          <div className="flex min-h-0 flex-1 overflow-hidden">
            <div className="w-[72px] shrink-0 overflow-y-auto border-r border-hairline">
              <div className="flex flex-col items-center gap-1 px-1.5 py-2">
                {BRANDING_TABS.map((tab) => {
                  const Icon = tab.icon
                  const isActive = activeTab === tab.key

                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => {
                        setActiveTab(tab.key)
                        if (tab.key !== 'caption') {
                          setIsCaptionEditorOpen(false)
                        }
                      }}
                      className={cn(
                        'flex w-full flex-col items-center gap-1 rounded-lg px-1 py-2 text-center transition-colors',
                        isActive
                          ? 'bg-neutral-800 text-neutral-100'
                          : 'text-neutral-300 hover:bg-white/6 hover:text-neutral-100',
                      )}
                      title={tab.label}
                    >
                      <Icon className="size-5 shrink-0" strokeWidth={1.25} />
                      <span className="w-full truncate text-[10px] font-medium leading-[1.2]">
                        {tab.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {loadingDetail ? (
                <div className="flex h-full items-center justify-center text-sm text-neutral-500">
                  加载中...
                </div>
              ) : detailError ? (
                <div className="flex h-full items-center justify-center text-sm text-neutral-500">
                  {detailError}
                </div>
              ) : !detail ? (
                <div className="flex h-full items-center justify-center text-sm text-neutral-500">
                  请先选择一个模板
                </div>
              ) : (
                <div className="px-4 pb-4 pt-3">
                  {activeTab === 'caption' ? (
                    isCaptionEditorOpen ? (
                      <CaptionEditorPanel
                        detail={detail}
                        captionConfig={localConfig.captions as Record<string, unknown> | undefined}
                        captionStyleKey={(localConfig.captionStyleKey as string) ?? 'blue'}
                        uploadedFonts={(localConfig.fonts as Array<{ assetId?: number; publicUrl?: string; fileName?: string; fontFamily?: string }>) ?? []}
                        onUpdateConfig={(updates) => {
                          const prevCaptions = (localConfig.captions as Record<string, unknown> | undefined) ?? { enabled: true }
                          updateLocalConfig({ captions: { ...prevCaptions, ...updates } })
                        }}
                        onBack={() => setIsCaptionEditorOpen(false)}
                      />
                    ) : (
                      <CaptionPanel
                        detail={detail}
                        captionsEnabled={(localConfig.captions as { enabled?: boolean } | undefined)?.enabled !== false}
                        onToggleCaptions={(enabled) => {
                          const prevCaptions = (localConfig.captions as Record<string, unknown> | undefined) ?? {}
                          updateLocalConfig({ captions: { ...prevCaptions, enabled } })
                        }}
                        selectedStyleKey={(localConfig.captionStyleKey as string) ?? 'blue'}
                        onSelectStyle={(key) => {
                          // 切换到不同样式时，用新样式的默认参数重新物化 captions，
                          // 丢弃上一个样式的样式覆盖（只保留 enabled / 位置 x,y,width）
                          const prevCaptions = (localConfig.captions as Record<string, unknown> | undefined) ?? {}
                          updateLocalConfig({
                            captionStyleKey: key,
                            captions: resetCaptionStyle(key, prevCaptions),
                          })
                        }}
                      />
                    )
                  ) : activeTab === 'audiogram' ? (
                    <AudiogramPanel
                      selectedKey={(localConfig.audiogramPreset as string) ?? 'spectrum'}
                      onSelect={(key) => {
                        setLocalConfig((prev) => {
                          const prevAudiogram = (prev.audiogram as Record<string, unknown> | undefined) ?? {}
                          return {
                            ...prev,
                            audiogramPreset: key,
                            audiogram: {
                              enabled: true,
                              ...prevAudiogram,
                            },
                          }
                        })
                      }}
                      enabled={(localConfig.audiogram as { enabled?: boolean } | undefined)?.enabled !== false}
                      onToggleEnabled={(enabled) => {
                        const prev = (localConfig.audiogram as Record<string, unknown> | undefined) ?? {}
                        updateLocalConfig({ audiogram: { ...prev, enabled } })
                      }}
                    />
                  ) : activeTab === 'text' ? (
                    isTextEditorOpen ? (
                      <TextEditorPanel
                        selectedKey={(localConfig.textPreset as string) ?? 'modern'}
                        textConfig={(localConfig.text as Record<string, unknown> | undefined) ?? undefined}
                        uploadedFonts={(localConfig.fonts as Array<{ assetId?: number; publicUrl?: string; fileName?: string; fontFamily?: string }>) ?? []}
                        activeTab={activeTextEditorTab}
                        onSwitchTab={setActiveTextEditorTab}
                        onUpdateConfig={(updates) => {
                          const prevText = (localConfig.text as Record<string, unknown> | undefined) ?? {}
                          updateLocalConfig({ text: { ...prevText, ...updates } })
                        }}
                        onBack={() => setIsTextEditorOpen(false)}
                        onDelete={() => {
                          updateLocalConfig({ textPreset: undefined, text: undefined })
                          setIsTextEditorOpen(false)
                        }}
                      />
                    ) : (
                      <TextPresetPanel
                        selectedKey={(localConfig.textPreset as string) ?? 'modern'}
                        onSelect={(key) => {
                          // 切换预设时用新预设的默认参数重新初始化 text，
                          // 丢弃上一个预设的样式覆盖（只保留 enabled / 位置 x,y,width,height）
                          const prevText = (localConfig.text as Record<string, unknown> | undefined) ?? {}
                          updateLocalConfig({
                            textPreset: key,
                            text: resetTextPreset(key, prevText),
                          })
                          setActiveTextEditorTab('style')
                          setIsTextEditorOpen(true)
                        }}
                      />
                    )
                  ) : activeTab === 'intro' ? (
                    <MotionPresetPanel
                      title="Intro"
                      description="上传品牌开场视频，会拼接到每次生成视频的开头。"
                      icon={Sparkles}
                      accept=".mp4,.mov,.webm"
                      hint="MP4 / MOV / WEBM · 建议不超过 30s"
                      uploadedFile={(localConfig.intro as { assetId?: number; publicUrl?: string; fileName?: string } | null) ?? null}
                      onUpload={(f) => updateLocalConfig({ intro: f })}
                      onRemove={() => updateLocalConfig({ intro: null })}
                      canUpload={hasBrandTemplateSubscription}
                      onSubscriptionRequired={() => setUpgradeDialogOpen(true)}
                    />
                  ) : activeTab === 'outro' ? (
                    <MotionPresetPanel
                      title="Outro"
                      description="上传结尾视频，会拼接到每次生成视频的末尾。"
                      icon={MonitorPlay}
                      accept=".mp4,.mov,.webm"
                      hint="MP4 / MOV / WEBM · 建议不超过 30s"
                      uploadedFile={(localConfig.outro as { assetId?: number; publicUrl?: string; fileName?: string } | null) ?? null}
                      onUpload={(f) => updateLocalConfig({ outro: f })}
                      onRemove={() => updateLocalConfig({ outro: null })}
                      canUpload={hasBrandTemplateSubscription}
                      onSubscriptionRequired={() => setUpgradeDialogOpen(true)}
                    />
                  ) : activeTab === 'logo' ? (
                    <LogoPanel
                      logos={(localConfig.logos as Array<{ assetId?: number; publicUrl?: string; fileName?: string; position?: string; scale?: string }>) ?? []}
                      onAddLogo={(logo) => updateLocalConfig({ logos: [...((localConfig.logos as unknown[]) ?? []), logo] })}
                      onRemoveLogo={(index) => {
                        const logos = (localConfig.logos as Array<Record<string, unknown>>) ?? []
                        updateLocalConfig({ logos: logos.filter((_, i) => i !== index) })
                      }}
                      onUpdateLogo={(index, updates) => {
                        const logos = (localConfig.logos as Array<Record<string, unknown>>) ?? []
                        updateLocalConfig({ logos: logos.map((l, i) => i === index ? { ...l, ...updates } : l) })
                      }}
                      enabled={(localConfig.logoEnabled as boolean | undefined) ?? true}
                      onToggleEnabled={(enabled) => updateLocalConfig({ logoEnabled: enabled })}
                    />
                  ) : activeTab === 'background' ? (
                    <BackgroundPanel
                      background={(localConfig.background as { assetId?: number; publicUrl?: string; fileName?: string } | null) ?? null}
                      onUpload={(f) => updateLocalConfig({ background: f })}
                      onRemove={() => updateLocalConfig({ background: null })}
                      enabled={(localConfig.backgroundEnabled as boolean | undefined) ?? true}
                      onToggleEnabled={(enabled) => updateLocalConfig({ backgroundEnabled: enabled })}
                    />
                  ) : activeTab === 'settings' ? (
                    <SettingsPanel detail={detail} localConfig={localConfig} onUpdate={updateLocalConfig} />
                  ) : activeTab === 'fonts' ? (
                    <FontsPanel
                      uploadedFonts={(localConfig.fonts as Array<{ assetId?: number; publicUrl?: string; fileName?: string; fontFamily?: string }>) ?? []}
                      onAddFont={(f) => updateLocalConfig({ fonts: [...((localConfig.fonts as unknown[]) ?? []), f] })}
                      onRemoveFont={(index) => {
                        const fonts = (localConfig.fonts as Array<Record<string, unknown>>) ?? []
                        updateLocalConfig({ fonts: fonts.filter((_, i) => i !== index) })
                      }}
                      onSelectFont={(family) => {
                        // 点击内置字体卡片 -> 设为当前字幕字体
                        const prevCaptions = (localConfig.captions as Record<string, unknown> | undefined) ?? { enabled: true }
                        updateLocalConfig({ captions: { ...prevCaptions, fontFamily: family } })
                      }}
                      canUpload={hasBrandTemplateSubscription}
                      onSubscriptionRequired={() => setUpgradeDialogOpen(true)}
                    />
                  ) : (
                    <PlaceholderPanel activeTab={activeTab} />
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        <aside className="hidden min-h-0 flex-col overflow-hidden rounded-2xl border border-hairline bg-surface-1 xl:flex">
          <div className="flex h-12 shrink-0 items-center border-b border-hairline px-4">
            <span className="text-sm font-semibold text-neutral-100">预览与摘要</span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-wrap gap-2 border-b border-hairline px-4 py-3">
              {summary.map((item) => (
                <span
                  key={`${item.label}-${item.value}`}
                  className="inline-flex items-center gap-1 rounded-full border border-hairline bg-neutral-800/80 px-2.5 py-1 text-[11px] leading-none text-neutral-200"
                >
                  <span className="text-neutral-400">{item.label}</span>
                  <span className="font-medium text-neutral-200">{item.value}</span>
                </span>
              ))}
            </div>

            <div className="space-y-4 p-4">
              <div className="rounded-2xl border border-hairline bg-surface-0 px-2 py-4 2xl:px-3">
                <div className="mx-auto flex w-full max-w-[300px] flex-col 2xl:max-w-[340px]">
                  {detail ? (
                    <TemplatePreview
                      config={previewConfig}
                      variantConfig={previewVariantConfig}
                      onOverlayChange={handleOverlayChange}
                    />
                  ) : (
                    <div className="relative aspect-[9/16] overflow-hidden rounded-sm bg-black">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(56,189,248,0.2),transparent_35%),linear-gradient(180deg,#111_0%,#060606_100%)]" />
                    </div>
                  )}
                </div>
              </div>

              <div className="grid gap-3">
                <MetricCard label="画幅比例" value={orientationLabel(detail?.aspectRatio ?? null)} note="当前模板默认输出方向" />
                <MetricCard label="分辨率" value={resolutionLabel(detail)} note="用于主流短视频平台的导出规格" />
                <MetricCard label="预设代码" value={detail?.presetCode ?? '-'} note="后续可以接模板默认渲染参数" />
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* 创建模板弹窗 */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[360px]">
          <DialogHeader>
            <DialogTitle>新建品牌模板</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-200">模板名称</label>
              <Input
                autoFocus
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && createName.trim()) void handleCreate() }}
                placeholder="输入模板名称"
                className="bg-[#181818]"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-200">方向</label>
              <div className="flex gap-2">
                {([
                  { value: '9:16', label: '竖版' },
                  { value: '1:1', label: '方形' },
                  { value: '16:9', label: '横版' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCreateAspect(opt.value)}
                    className={cn(
                      'flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                      createAspect === opt.value ? 'border-sky-600 bg-sky-500/10 text-sky-300' : 'border-hairline bg-[#181818] text-neutral-300 hover:bg-neutral-800',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {saveError ? <p className="text-xs text-red-400">{saveError}</p> : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setCreateDialogOpen(false)}>取消</Button>
            <Button size="sm" disabled={!createName.trim() || creating} onClick={() => void handleCreate()}>
              {creating ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
              {creating ? '创建中…' : '创建'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 重命名弹窗 */}
      <Dialog open={Boolean(renameTarget)} onOpenChange={(open) => { if (!open) setRenameTarget(null) }}>
        <DialogContent className="sm:max-w-[360px]">
          <DialogHeader>
            <DialogTitle>重命名模板</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Input
              autoFocus
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && renameName.trim()) void handleRename() }}
              className="bg-[#181818]"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setRenameTarget(null)}>取消</Button>
            <Button size="sm" disabled={!renameName.trim() || renaming} onClick={() => void handleRename()}>
              {renaming ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
              {renaming ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认弹窗 */}
      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <AlertDialogContent className="sm:max-w-[360px]">
          <AlertDialogHeader>
            <AlertDialogTitle>删除模板</AlertDialogTitle>
          </AlertDialogHeader>
          <p className="py-2 text-sm text-neutral-300">
            确定删除「{deleteTarget?.templateName}」吗？此操作不可撤销。
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={() => void handleDelete()}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {deleting ? '删除中…' : '删除'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WorkspaceUpgradeDialog
        open={upgradeDialogOpen}
        onOpenChange={setUpgradeDialogOpen}
        membership={membership}
      />
    </div>
  )
}

/** 模板卡片右上角的更多操作菜单（hover 时显示） */
function TemplateCardMenu({ onRename, onDelete }: {
  onRename: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div
      ref={ref}
      className="absolute right-1.5 top-1.5 z-20"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex size-6 items-center justify-center rounded-md bg-black/70 text-white/70 backdrop-blur-sm transition-colors hover:bg-black/90 hover:text-white',
          open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        )}
      >
        <MoreVertical className="size-3.5" />
      </button>
      {open ? (
        <div className="absolute right-0 top-7 w-28 overflow-hidden rounded-lg border border-hairline bg-neutral-800 py-1 shadow-lg">
          <button
            type="button"
            onClick={() => { setOpen(false); onRename() }}
            className="flex w-full items-center px-3 py-1.5 text-xs text-neutral-200 transition-colors hover:bg-neutral-700"
          >
            重命名
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onDelete() }}
            className="flex w-full items-center px-3 py-1.5 text-xs text-red-400 transition-colors hover:bg-neutral-700"
          >
            删除
          </button>
        </div>
      ) : null}
    </div>
  )
}

function CaptionPanel({
  detail,
  captionsEnabled,
  onToggleCaptions,
  selectedStyleKey,
  onSelectStyle,
}: {
  detail: Template
  captionsEnabled: boolean
  onToggleCaptions: (enabled: boolean) => void
  selectedStyleKey: string
  onSelectStyle: (key: string) => void
}) {

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between border-b border-hairline pb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-neutral-800 p-2 text-neutral-200">
            <Captions className="size-4" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-sm font-semibold text-neutral-100">字幕</div>
            <div className="text-xs text-neutral-400">
              为 {detail.templateName} 选择默认字幕风格 · 共 {captionLibraryStyles.length} 个样式
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-neutral-200">启用字幕</span>
          <Switch checked={captionsEnabled} onCheckedChange={onToggleCaptions} />
        </div>
      </div>

      {/* 66 个字幕样式网格，hover 播放 mp4 动画 */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(124px,1fr))] gap-3">
        {captionLibraryStyles.map((style) => {
          const isActive = selectedStyleKey === style.key
          return (
            <button
              key={style.key}
              type="button"
              onClick={() => onSelectStyle(style.key)}
              className={cn(
                'flex flex-col overflow-hidden rounded-xl text-left transition-shadow hover:shadow-md',
                isActive ? 'ring-2 ring-sky-600' : 'ring-1 ring-hairline',
              )}
            >
              <div className="h-[62px] overflow-hidden rounded-t-xl bg-neutral-900">
                <video
                  loop
                  playsInline
                  muted
                  preload="none"
                  poster={`${TEMPLATE_BASE}/${style.key}.jpg`}
                  className="h-full w-full object-cover"
                  onMouseEnter={(e) => {
                    const v = e.currentTarget
                    // preload="none" 时需先 load 再 play
                    if (v.readyState === 0) {
                      v.load()
                    }
                    v.play().catch(() => {})
                  }}
                  onMouseLeave={(e) => {
                    const v = e.currentTarget
                    v.pause()
                  }}
                >
                  <source src={`${TEMPLATE_BASE}/${style.key}.mp4`} type="video/mp4" />
                </video>
              </div>
              <div className="flex flex-1 items-center justify-center rounded-b-xl bg-neutral-800/70 py-1.5">
                <h3 className="truncate px-1 text-center text-[10px] font-medium text-neutral-200">
                  {style.title}
                </h3>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function CaptionEditorPanel({
  detail,
  captionConfig,
  captionStyleKey,
  uploadedFonts,
  onUpdateConfig,
  onBack,
}: {
  detail: Template
  captionConfig: Record<string, unknown> | undefined
  captionStyleKey: string
  uploadedFonts: Array<{ assetId?: number; publicUrl?: string; fileName?: string; fontFamily?: string }>
  onUpdateConfig: (updates: Record<string, unknown>) => void
  onBack: () => void
}) {
  const config = captionConfig ?? {}
  // fallback 从选中样式派生，而不是硬编码默认值 —— 这样打开 beasty 的编辑器会看到 strokeWidth=5 而不是 0
  const styleBase = getCaptionStyleConfig(captionStyleKey)
  const styleRendered = buildCaptionRenderConfig(styleBase, config)
  const family = getConfigString(config, ['fontFamily', 'family'], styleRendered.fontFamily)
  const fontColor = getConfigString(config, ['fontColor', 'color'], styleRendered.color)
  const highlightColor = getConfigString(config, ['highlightColor', 'accentColor'], styleRendered.highlightColor ?? '#FFD93D')
  const strokeColor = getConfigString(config, ['strokeColor'], styleRendered.strokeColor)
  const strokeWidth = getConfigNumber(config, ['strokeWidth'], styleRendered.strokeWidth)
  const fontSize = getConfigNumber(config, ['fontSize'], styleRendered.fontSize)
  const fontWeight = getConfigNumber(config, ['fontWeight'], styleRendered.fontWeight)
  const maxWords = getConfigNumber(config, ['maxWordsPerLine', 'maxWords'], styleRendered.maxWordsPerLine)
  const letterSpacing = getConfigNumber(config, ['letterSpacing'], styleRendered.letterSpacing)
  const lineHeight = getConfigNumber(config, ['lineHeight'], styleRendered.lineHeight)
  const textAlign = getConfigString(config, ['textAlign'], styleRendered.textAlign) as 'left' | 'center' | 'right'
  const textTransform = getConfigString(config, ['textTransform'], styleRendered.textTransform) as 'none' | 'uppercase'
  const italic = getConfigBool(config, ['italic'], styleRendered.italic)
  // shadow：开关控制 textShadow 是否存在，而不是单独的 shadowEnabled 字段
  const hasShadow = Boolean(getConfigString(config, ['textShadow'], styleRendered.textShadow))
  const wordAnimation = getConfigString(config, ['wordAnimation', 'animation'], styleRendered.wordAnimation)
  const highlightMode = getConfigString(config, ['highlightMode'], styleRendered.highlightMode) as 'fill' | 'box' | 'underline' | 'glow'

  // 字体下拉选项：内置 + 上传字体合并去重（和 Text 编辑器一致）
  const fontOptions = Array.from(
    new Set([
      ...TEXT_EDITOR_FONT_OPTIONS,
      ...uploadedFonts.map((font) => font.fontFamily).filter((f): f is string => Boolean(f && f.trim())),
    ]),
  )

  const WORD_ANIMATION_OPTIONS: Array<{ key: string; label: string }> = [
    { key: 'pop', label: '弹出' },
    { key: 'fade', label: '淡入' },
    { key: 'slide', label: '滑入' },
    { key: 'scale', label: '缩放' },
    { key: 'glitch', label: '故障' },
    { key: 'wiggle', label: '摆动' },
    { key: 'stretch', label: '拉伸' },
    { key: 'pulse', label: '脉冲' },
    { key: 'bounce', label: '弹跳' },
    { key: 'blur-in', label: '模糊淡入' },
    { key: 'flip-up', label: '向上翻转' },
    { key: 'drift', label: '漂移' },
  ]
  const HIGHLIGHT_MODE_OPTIONS: Array<{ key: string; label: string }> = [
    { key: 'fill', label: '填充' },
    { key: 'box', label: '色块' },
    { key: 'underline', label: '下划线' },
    { key: 'glow', label: '发光' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 border-b border-hairline pb-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-full border border-hairline bg-surface-0 px-3 py-1.5 text-xs font-medium text-neutral-200 transition-colors hover:bg-neutral-800"
        >
          返回
        </button>
        <div>
          <div className="text-base font-semibold text-neutral-100">编辑字幕样式</div>
          <div className="text-xs text-neutral-400">
            {detail.templateName} · {captionStyleKey}
          </div>
        </div>
      </div>

      <EditorSection title="字体">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_140px]">
          <select
            value={family}
            onChange={(e) => onUpdateConfig({ fontFamily: e.target.value })}
            className="h-10 w-full rounded-xl border border-hairline bg-[#181818] px-3 text-sm text-neutral-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          >
            {fontOptions.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
          <FontWeightSelect value={fontWeight} onChange={(v) => onUpdateConfig({ fontWeight: v })} />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-xs text-neutral-500">字号</span>
          <input
            type="number"
            min={14}
            max={80}
            value={fontSize}
            onChange={(e) => onUpdateConfig({ fontSize: Number(e.target.value) })}
            className="h-8 w-16 rounded-full border border-hairline bg-surface-0 px-3 text-center text-xs text-neutral-200 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
          <MiniPill active={italic} onClick={() => onUpdateConfig({ italic: !italic })}>斜体</MiniPill>
          <MiniPill active={textTransform === 'uppercase'} onClick={() => onUpdateConfig({ textTransform: textTransform === 'uppercase' ? 'none' : 'uppercase' })}>大写</MiniPill>
        </div>
      </EditorSection>

      <EditorSection title="对齐">
        <div className="flex gap-2">
          {(['left', 'center', 'right'] as const).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onUpdateConfig({ textAlign: item })}
              className={cn(
                'inline-flex h-10 flex-1 items-center justify-center rounded-xl border border-hairline bg-[#181818] text-sm capitalize text-neutral-300 transition-colors hover:bg-neutral-800',
                textAlign === item ? 'border-sky-600 text-white ring-1 ring-sky-600' : '',
              )}
            >
              {item === 'left' ? '左对齐' : item === 'center' ? '居中' : '右对齐'}
            </button>
          ))}
        </div>
      </EditorSection>

      <EditorSection title="颜色">
        <div className="grid gap-4 md:grid-cols-2">
          <EditableColorField label="字体颜色" value={fontColor} onChange={(v) => onUpdateConfig({ color: v })} />
          <EditableColorField label="高亮颜色" value={highlightColor} onChange={(v) => onUpdateConfig({ highlightColor: v })} />
        </div>
      </EditorSection>

      <EditorSection title="间距">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="mb-2 flex items-center justify-between text-sm text-neutral-300">
              <span>行高</span>
              <span className="text-xs text-neutral-500">{lineHeight.toFixed(2)}</span>
            </div>
            <input type="range" min={0.8} max={1.6} step={0.02} value={lineHeight} onChange={(e) => onUpdateConfig({ lineHeight: Number(e.target.value) })} className="w-full accent-sky-500" />
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between text-sm text-neutral-300">
              <span>字间距</span>
              <span className="text-xs text-neutral-500">{letterSpacing.toFixed(2)}em</span>
            </div>
            <input type="range" min={-0.08} max={0.12} step={0.01} value={letterSpacing} onChange={(e) => onUpdateConfig({ letterSpacing: Number(e.target.value) })} className="w-full accent-sky-500" />
          </div>
        </div>
      </EditorSection>

      <EditorSection title="最大词数">
        <RangeRow value={maxWords} min={1} max={8} step={1} onChange={(v) => onUpdateConfig({ maxWordsPerLine: v })} />
      </EditorSection>

      <EditorSection title="描边" trailing={<Switch checked={strokeWidth > 0} onCheckedChange={(checked) => onUpdateConfig({ strokeWidth: checked ? 2 : 0 })} />}>
        {strokeWidth > 0 ? (
          <>
            <EditableColorField label="描边颜色" value={strokeColor} onChange={(v) => onUpdateConfig({ strokeColor: v })} />
            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between text-sm text-neutral-300">
                <span>粗细</span>
                <span className="text-xs text-neutral-500">{strokeWidth}</span>
              </div>
              <input
                type="range"
                min={1}
                max={10}
                value={strokeWidth}
                onChange={(e) => onUpdateConfig({ strokeWidth: Number(e.target.value) })}
                className="w-full accent-sky-500"
              />
            </div>
          </>
        ) : null}
      </EditorSection>

      <EditorSection title="阴影" trailing={
        <Switch
          checked={hasShadow}
          onCheckedChange={(checked) => onUpdateConfig({ textShadow: checked ? '0 2px 10px rgba(0,0,0,0.45)' : 'none' })}
        />
      }>
        {hasShadow ? (
          <div className="flex flex-wrap gap-2">
            {[
              { label: '弱', value: '0 1px 4px rgba(0,0,0,0.3)' },
              { label: '中', value: '0 2px 10px rgba(0,0,0,0.45)' },
              { label: '强', value: '0 4px 18px rgba(0,0,0,0.6)' },
            ].map((item) => (
              <MiniPill
                key={item.label}
                active={getConfigString(config, ['textShadow'], styleRendered.textShadow) === item.value}
                onClick={() => onUpdateConfig({ textShadow: item.value })}
              >
                {item.label}
              </MiniPill>
            ))}
          </div>
        ) : null}
      </EditorSection>

      <EditorSection title="逐词动画">
        <div className="flex flex-wrap gap-2">
          {WORD_ANIMATION_OPTIONS.map((item) => (
            <MiniPill key={item.key} active={wordAnimation === item.key} onClick={() => onUpdateConfig({ wordAnimation: item.key })}>
              {item.label}
            </MiniPill>
          ))}
        </div>
      </EditorSection>

      <EditorSection title="高亮模式">
        <div className="flex flex-wrap gap-2">
          {HIGHLIGHT_MODE_OPTIONS.map((item) => (
            <MiniPill key={item.key} active={highlightMode === item.key} onClick={() => onUpdateConfig({ highlightMode: item.key })}>
              {item.label}
            </MiniPill>
          ))}
        </div>
      </EditorSection>
    </div>
  )
}

function AudiogramPanel({ selectedKey, onSelect, enabled, onToggleEnabled }: {
  selectedKey: string
  onSelect: (key: string) => void
  enabled: boolean
  onToggleEnabled: (enabled: boolean) => void
}) {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between border-b border-hairline pb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-neutral-800 p-2 text-neutral-200">
            <AudioWaveform className="size-4" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-sm font-semibold text-neutral-100">波形</div>
            <div className="text-xs text-neutral-400">为音频内容选择波形显示样式。</div>
          </div>
        </div>
        <Switch checked={enabled} onCheckedChange={onToggleEnabled} />
      </div>

      <div>
        <div className="mb-3 text-sm font-semibold text-neutral-100">预设</div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {AUDIOGRAM_PRESETS.map((preset) => {
            const isActive = selectedKey === preset.key
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => onSelect(preset.key)}
                className={cn(
                  'overflow-hidden rounded-2xl border text-left transition-colors',
                  isActive ? 'border-sky-600 ring-1 ring-sky-600' : 'border-hairline hover:border-neutral-600',
                )}
              >
                <div className="flex h-20 items-center justify-center bg-[#2d2d2d]">
                  <AudiogramGlyph type={preset.key} />
                </div>
                <div className="border-t border-hairline bg-neutral-800/70 px-4 py-3 text-center text-sm font-medium text-neutral-200">
                  {preset.label}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function TextPresetPanel({ selectedKey, onSelect }: {
  selectedKey: string
  onSelect: (key: string) => void
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 border-b border-hairline pb-3">
        <div className="rounded-lg bg-neutral-800 p-2 text-neutral-200">
          <Type className="size-4" strokeWidth={1.8} />
        </div>
        <div>
          <div className="text-sm font-semibold text-neutral-100">文字</div>
          <div className="text-xs text-neutral-400">快速应用片头文案和标题样式。</div>
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-3 2xl:grid-cols-4">
        {TEXT_PRESETS.map((preset) => {
          const isActive = selectedKey === preset.key
          return (
            <button
              key={preset.key}
              type="button"
              onClick={() => onSelect(preset.key)}
              className={cn(
                'overflow-hidden rounded-xl border text-left transition-colors',
                isActive ? 'border-sky-600 ring-1 ring-sky-600' : 'border-hairline hover:border-neutral-600',
              )}
            >
              <div
                className={cn(
                  'flex h-16 items-center justify-center bg-[#111] px-3 text-center text-base font-bold leading-none tracking-tight text-white',
                  preset.accent,
                )}
              >
                <div className="whitespace-pre-line">{preset.sample}</div>
              </div>
              <div className="bg-neutral-800/70 px-3 py-1.5 text-center">
                <div className="truncate text-[11px] font-semibold text-neutral-100">{preset.name}</div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function TextEditorPanel({
  selectedKey,
  textConfig,
  uploadedFonts,
  activeTab,
  onSwitchTab,
  onUpdateConfig,
  onBack,
  onDelete,
}: {
  selectedKey: string
  textConfig: Record<string, unknown> | undefined
  uploadedFonts: Array<{ assetId?: number; publicUrl?: string; fileName?: string; fontFamily?: string }>
  activeTab: TextEditorTab
  onSwitchTab: (tab: TextEditorTab) => void
  onUpdateConfig: (updates: Record<string, unknown>) => void
  onBack: () => void
  onDelete: () => void
}) {
  const config = textConfig ?? {}
  const [previewRuns, setPreviewRuns] = useState<Record<string, number>>({})
  const [animExpanded, setAnimExpanded] = useState(true)
  // fallback 从选中预设的默认值派生，保证编辑器显示和预览一致
  const presetDefaults = buildTextPresetDefaults(selectedKey)
  const content = getConfigString(config, ['content'], presetDefaults.content)
  const fontFamily = getConfigString(config, ['fontFamily'], presetDefaults.fontFamily)
  const align = getConfigString(config, ['align'], presetDefaults.align)
  const color = getConfigString(config, ['color'], presetDefaults.color)
  const accentColor = getConfigString(config, ['accentColor'], presetDefaults.accentColor)
  const rotation = getConfigNumber(config, ['rotation'], presetDefaults.rotation)
  const fontWeight = getConfigNumber(config, ['fontWeight'], presetDefaults.fontWeight)
  const fontSize = getConfigNumber(config, ['fontSize'], presetDefaults.fontSize)
  const lineHeight = getConfigNumber(config, ['lineHeight'], presetDefaults.lineHeight)
  const letterSpacing = getConfigNumber(config, ['letterSpacing'], presetDefaults.letterSpacing)
  const italic = getConfigBool(config, ['italic'], presetDefaults.italic)
  const uppercase = getConfigBool(config, ['uppercase'], presetDefaults.uppercase)
  const animation = getConfigString(config, ['animation'], presetDefaults.animation)
  const isGradientLike = selectedKey === 'gradient' || selectedKey === 'highlight'
  const fontOptions = Array.from(
    new Set([
      ...TEXT_EDITOR_FONT_OPTIONS,
      ...uploadedFonts.map((font) => font.fontFamily).filter((family): family is string => Boolean(family && family.trim())),
    ]),
  )
  const currentAnimName = TEXT_ANIMATION_OPTIONS.find((item) => item.key === animation)?.name ?? '无'

  return (
    <div className="space-y-3">
      {/* 工具栏 */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 rounded-full border border-hairline bg-neutral-800/80 px-3 py-1.5 text-xs font-medium text-neutral-200 transition-colors hover:bg-neutral-700"
        >
          <ArrowLeft className="size-3.5" />
          返回
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label="删除文字图层"
          className="inline-flex size-7 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-white/6 hover:text-red-400"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>

      {/* 文本输入 */}
      <textarea
        value={content}
        onChange={(e) => onUpdateConfig({ content: e.target.value })}
        placeholder="在这里输入文字..."
        className="min-h-[72px] w-full resize-none rounded-lg border border-neutral-700 bg-neutral-800 px-4 py-3 text-sm text-neutral-200 placeholder:text-neutral-400 focus:border-neutral-600 focus:outline-none focus:ring-0"
      />

      {/* Tab 切换器 */}
      <div className="grid h-9 grid-cols-2 gap-1 rounded-full bg-neutral-900 p-1">
        <button
          type="button"
          onClick={() => onSwitchTab('animation')}
          className={cn(
            'inline-flex items-center justify-center gap-2 rounded-full px-3 py-1 text-[13px] font-medium transition-all',
            activeTab === 'animation' ? 'bg-neutral-700 text-white shadow-sm' : 'text-neutral-300 hover:text-neutral-100',
          )}
        >
          <Sparkles className="size-3.5" />
          动画
        </button>
        <button
          type="button"
          onClick={() => onSwitchTab('style')}
          className={cn(
            'inline-flex items-center justify-center gap-2 rounded-full px-3 py-1 text-[13px] font-medium transition-all',
            activeTab === 'style' ? 'bg-neutral-700 text-white shadow-sm' : 'text-neutral-300 hover:text-neutral-100',
          )}
        >
          <Palette className="size-3.5" />
          样式
        </button>
      </div>

      {/* Animation tab */}
      {activeTab === 'animation' ? (
        <div className="mt-4 space-y-4">
          {/* 可折叠 section: Enter Animation */}
          <section className="overflow-hidden rounded-md border border-hairline bg-neutral-900">
            <button
              type="button"
              onClick={() => setAnimExpanded((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-white/3"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-neutral-100">入场动画</span>
                <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-neutral-800 px-1.5 text-[12px] font-medium text-neutral-400 ring-1 ring-inset ring-white/5">
                  {TEXT_ANIMATION_OPTIONS.length}
                </span>
                <span className="text-[12px] capitalize text-neutral-500">{currentAnimName}</span>
              </div>
              <ChevronDown className={cn('size-3.5 text-neutral-400 transition-transform', animExpanded && 'rotate-180')} />
            </button>
            {animExpanded ? (
              <div className="p-3">
                <div className="grid grid-cols-6 gap-2">
                  {TEXT_ANIMATION_OPTIONS.map((item) => {
                    const isActive = animation === item.key
                    const runId = previewRuns[item.key] ?? 0
                    const sampleAnimation = normalizeTextAnimationSample(item.key)
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => onUpdateConfig({ animation: item.key })}
                        onMouseEnter={() => {
                          setPreviewRuns((prev) => ({ ...prev, [item.key]: (prev[item.key] ?? 0) + 1 }))
                        }}
                        className={cn(
                          'group relative flex flex-col gap-1.5 rounded-xl p-1.5 text-left transition-all',
                          isActive
                            ? 'bg-sky-500/5 ring-2 ring-sky-400'
                            : 'bg-neutral-900/40 ring-1 ring-inset ring-white/5 hover:ring-white/10',
                        )}
                      >
                        <div className="relative flex aspect-[5/3] w-full items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-neutral-800 to-neutral-900">
                          <span
                            key={`${item.key}-${runId}`}
                            className="text-[18px] font-semibold tracking-tight text-white"
                            style={{
                              fontFamily: resolveTextEditorFontStack(fontFamily),
                              animation: sampleAnimation === 'none' ? undefined : `textPreview${sampleAnimation.replace(/(^|-)(\w)/g, (_, __, c: string) => c.toUpperCase())} 720ms cubic-bezier(.2,.8,.2,1) both`,
                            }}
                          >
                            Aa
                          </span>
                          {isActive ? (
                            <span className="absolute right-1 top-1 inline-flex size-4 items-center justify-center rounded-full bg-sky-400 text-neutral-900">
                              <Check className="size-2.5" />
                            </span>
                          ) : null}
                        </div>
                        <div className="px-1 pb-0.5">
                          <div className="truncate text-[11px] font-medium leading-tight text-neutral-100">{item.name}</div>
                          <div className="truncate text-[9px] text-neutral-500">{item.note}</div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : null}
          </section>

          <style>{`
            @keyframes textPreviewFade { 0% { opacity: 0; } 100% { opacity: 1; } }
            @keyframes textPreviewScale { 0% { opacity: 0; transform: scale(0.68); } 65% { opacity: 1; transform: scale(1.06); } 100% { opacity: 1; transform: scale(1); } }
            @keyframes textPreviewBounce { 0% { opacity: 0; transform: scale(0.5) translateY(14px); } 55% { opacity: 1; transform: scale(1.08) translateY(-4px); } 100% { opacity: 1; transform: scale(1) translateY(0); } }
            @keyframes textPreviewFlip { 0% { opacity: 0; transform: perspective(340px) rotateX(-72deg); } 100% { opacity: 1; transform: perspective(340px) rotateX(0); } }
            @keyframes textPreviewZoom { 0% { opacity: 0; transform: scale(1.45); filter: blur(10px); } 100% { opacity: 1; transform: scale(1); filter: blur(0); } }
            @keyframes textPreviewSlide { 0% { opacity: 0; transform: translateY(18px); } 100% { opacity: 1; transform: translateY(0); } }
            @keyframes textPreviewSnap { 0% { opacity: 0; transform: scale(0.92) rotate(-6deg); } 65% { opacity: 1; transform: scale(1.02) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
            @keyframes textPreviewGlitch { 0% { opacity: 0; transform: translate(-6px, 3px); } 25% { transform: translate(4px, -2px); } 50% { transform: translate(-2px, -1px); } 100% { opacity: 1; transform: translate(0, 0); } }
            @keyframes textPreviewSwipe { 0% { opacity: 0; clip-path: inset(0 100% 0 0); } 100% { opacity: 1; clip-path: inset(0 0 0 0); } }
            @keyframes textPreviewFloat { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: translateY(0); } }
            @keyframes textPreviewSpin { 0% { opacity: 0; transform: rotate(-18deg) scale(0.86); } 100% { opacity: 1; transform: rotate(0) scale(1); } }
          `}</style>
        </div>
      ) : (
        /* Style tab */
        <div className="mt-4 space-y-4">
          <div className="rounded-lg border border-hairline bg-neutral-900 p-4">
            <div className="mb-4 text-sm font-semibold text-neutral-100">排版</div>
            <div className="space-y-4">
              <div>
                <div className="mb-2 text-sm text-neutral-300">字体</div>
                <select
                  value={fontFamily}
                  onChange={(e) => onUpdateConfig({ fontFamily: e.target.value })}
                  className="h-10 w-full rounded-xl border border-hairline bg-[#181818] px-3 text-sm text-neutral-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                >
                  {fontOptions.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="mb-2 text-sm text-neutral-300">对齐方式</div>
                <div className="flex gap-2">
                  {[
                    { key: 'left', icon: AlignLeft },
                    { key: 'center', icon: AlignCenter },
                    { key: 'right', icon: AlignRight },
                  ].map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => onUpdateConfig({ align: item.key })}
                      className={cn(
                        'inline-flex size-10 items-center justify-center rounded-xl border border-hairline bg-[#181818] text-sm text-neutral-300 transition-colors hover:bg-neutral-800',
                        align === item.key ? 'border-sky-600 text-white ring-1 ring-sky-600' : '',
                      )}
                    >
                      <item.icon className="size-4" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm text-neutral-300">
                    <span>字号</span>
                    <span className="text-xs text-neutral-500">{fontSize}px</span>
                  </div>
                  <input type="range" min={18} max={64} step={1} value={fontSize} onChange={(e) => onUpdateConfig({ fontSize: Number(e.target.value) })} className="w-full accent-sky-500" />
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm text-neutral-300">
                    <span>字重</span>
                    <span className="text-xs text-neutral-500">{fontWeight}</span>
                  </div>
                  <input type="range" min={400} max={900} step={100} value={fontWeight} onChange={(e) => onUpdateConfig({ fontWeight: Number(e.target.value) })} className="w-full accent-sky-500" />
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm text-neutral-300">
                    <span>行高</span>
                    <span className="text-xs text-neutral-500">{lineHeight.toFixed(2)}</span>
                  </div>
                  <input type="range" min={0.8} max={1.4} step={0.02} value={lineHeight} onChange={(e) => onUpdateConfig({ lineHeight: Number(e.target.value) })} className="w-full accent-sky-500" />
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm text-neutral-300">
                    <span>字间距</span>
                    <span className="text-xs text-neutral-500">{letterSpacing.toFixed(2)}em</span>
                  </div>
                  <input type="range" min={-0.08} max={0.12} step={0.01} value={letterSpacing} onChange={(e) => onUpdateConfig({ letterSpacing: Number(e.target.value) })} className="w-full accent-sky-500" />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm text-neutral-300">
                    <span>旋转</span>
                    <span className="text-xs text-neutral-500">{rotation}deg</span>
                  </div>
                  <input type="range" min={-25} max={25} step={1} value={rotation} onChange={(e) => onUpdateConfig({ rotation: Number(e.target.value) })} className="w-full accent-sky-500" />
                </div>
                <div>
                  <div className="mb-2 text-sm text-neutral-300">文字样式</div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => onUpdateConfig({ italic: !italic })}
                      className={cn(
                        'inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl border border-hairline bg-[#181818] text-sm text-neutral-300 transition-colors hover:bg-neutral-800',
                        italic ? 'border-sky-600 text-white ring-1 ring-sky-600' : '',
                      )}
                    >
                      <Italic className="size-4" />
                      斜体
                    </button>
                    <button
                      type="button"
                      onClick={() => onUpdateConfig({ uppercase: !uppercase })}
                      className={cn(
                        'inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl border border-hairline bg-[#181818] text-sm text-neutral-300 transition-colors hover:bg-neutral-800',
                        uppercase ? 'border-sky-600 text-white ring-1 ring-sky-600' : '',
                      )}
                    >
                      <CaseSensitive className="size-4" />
                      大写
                    </button>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-hairline bg-[#181818] p-3">
                <div className="mb-2 text-xs text-neutral-400">实时预览</div>
                <div
                  style={{
                    fontFamily: resolveTextEditorFontStack(fontFamily),
                    fontSize: `${Math.max(20, fontSize * 0.7)}px`,
                    fontWeight,
                    lineHeight,
                    letterSpacing: `${letterSpacing}em`,
                    fontStyle: italic ? 'italic' : 'normal',
                    textTransform: uppercase ? 'uppercase' : 'none',
                    color,
                  }}
                  className="text-balance text-neutral-100"
                >
                  文字预览示例
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-hairline bg-neutral-900 p-4">
            <div className="mb-3 text-sm font-semibold text-neutral-100">颜色</div>
            {isGradientLike ? (
            <div className="text-sm text-neutral-400">渐变文字样式暂不支持单独设置颜色</div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <EditableColorField label="颜色" value={color} onChange={(v) => onUpdateConfig({ color: v })} />
                <EditableColorField label="高亮" value={accentColor} onChange={(v) => onUpdateConfig({ accentColor: v })} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function MotionPresetPanel({
  title,
  description,
  icon: Icon,
  accept,
  hint,
  uploadedFile,
  onUpload,
  onRemove,
  canUpload,
  onSubscriptionRequired,
}: {
  title: string
  description: string
  icon: typeof Sparkles
  accept: string
  hint: string
  uploadedFile: { assetId?: number; publicUrl?: string; fileName?: string } | null
  onUpload: (file: { assetId: number; publicUrl: string; fileName: string }) => void
  onRemove: () => void
  canUpload: boolean
  onSubscriptionRequired: () => void
}) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function handleFile(file: File) {
    if (!canUpload) {
      onSubscriptionRequired()
      return
    }
    setUploading(true); setError(null)
    try {
      const res = await uploadFile<ApiResponse<AssetUploadResult>>('/api/assets/upload?purpose=brand_template', file)
      onUpload({ assetId: res.data.id, publicUrl: res.data.publicUrl, fileName: res.data.fileName })
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败')
    } finally { setUploading(false) }
  }

  const isVideo = accept.includes('mp4') || accept.includes('mov')
  const requestUpload = () => {
    if (!canUpload) {
      onSubscriptionRequired()
      return
    }
    fileInputRef.current?.click()
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between border-b border-hairline pb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-neutral-800 p-2 text-neutral-200">
            <Icon className="size-4" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-sm font-semibold text-neutral-100">{title}</div>
            <div className="text-xs text-neutral-400">{description}</div>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = '' }} />
        <button type="button" disabled={uploading} onClick={requestUpload} className="inline-flex h-8 items-center gap-1 rounded-full border border-hairline bg-neutral-800 px-3 text-xs font-medium text-neutral-100 transition-colors hover:bg-neutral-700 disabled:opacity-50">
          {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
          {uploading ? '上传中…' : '上传'}
        </button>
      </div>

      {error ? <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-2 text-xs text-red-400">{error}</div> : null}

      {/* 已上传的文件 */}
      {uploadedFile ? (
        <div className="flex items-center gap-3 rounded-xl border border-hairline bg-surface-0 p-3">
          <div className="size-12 shrink-0 overflow-hidden rounded-md bg-neutral-900">
            {uploadedFile.publicUrl ? (
              isVideo
                ? <video src={resolveUrl(uploadedFile.publicUrl) ?? ''} className="size-full object-cover" muted />
                : <img src={resolveUrl(uploadedFile.publicUrl) ?? ''} alt={uploadedFile.fileName ?? ''} className="size-full object-cover" />
            ) : null}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium text-neutral-200">{uploadedFile.fileName ?? '已上传'}</div>
            <div className="text-[11px] text-neutral-500">{hint}</div>
          </div>
          <button type="button" onClick={onRemove} className="rounded p-1 text-neutral-500 hover:bg-neutral-800 hover:text-red-400"><Trash2 className="size-3.5" /></button>
        </div>
      ) : (
        <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-hairline bg-surface-0 text-center">
          <div className="rounded-xl bg-neutral-800 p-3 text-neutral-400">
            <Icon className="size-6" strokeWidth={1.5} />
          </div>
          <div className="text-sm font-medium text-neutral-300">还没有{title}文件</div>
          <div className="max-w-xs text-xs text-neutral-500">{hint}</div>
          <button type="button" disabled={uploading} onClick={requestUpload} className="mt-1 inline-flex h-8 items-center gap-1 rounded-full border border-hairline bg-neutral-800 px-4 text-xs font-medium text-neutral-100 transition-colors hover:bg-neutral-700 disabled:opacity-50">
            {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
            {uploading ? '上传中…' : `上传${title}`}
          </button>
        </div>
      )}
    </div>
  )
}

function LogoPanel({ logos, onAddLogo, onRemoveLogo, onUpdateLogo, enabled, onToggleEnabled }: {
  logos: Array<{ assetId?: number; publicUrl?: string; fileName?: string; position?: string; scale?: string }>
  onAddLogo: (logo: { assetId: number; publicUrl: string; fileName: string; position: string; scale: string }) => void
  onRemoveLogo: (index: number) => void
  onUpdateLogo: (index: number, updates: Record<string, unknown>) => void
  enabled: boolean
  onToggleEnabled: (enabled: boolean) => void
}) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function handleFile(file: File) {
    setUploading(true); setError(null)
    try {
      const res = await uploadFile<ApiResponse<AssetUploadResult>>('/api/assets/upload', file)
      onAddLogo({ assetId: res.data.id, publicUrl: res.data.publicUrl, fileName: res.data.fileName, position: 'bottom-right', scale: 'medium' })
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败')
    } finally { setUploading(false) }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between border-b border-hairline pb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-neutral-800 p-2 text-neutral-200">
            <Palette className="size-4" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-sm font-semibold text-neutral-100">logo</div>
            <div className="text-xs text-neutral-400">上传logo后可在右侧预览图上直接拖动位置。</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={enabled} onCheckedChange={onToggleEnabled} />
          <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = '' }} />
          <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="inline-flex h-8 items-center gap-1 rounded-full border border-hairline bg-neutral-800 px-3 text-xs font-medium text-neutral-100 transition-colors hover:bg-neutral-700 disabled:opacity-50">
            {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
            {uploading ? '上传中…' : '上传'}
          </button>
        </div>
      </div>

      {error ? <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-2 text-xs text-red-400">{error}</div> : null}

      {/* 已上传的 Logo 列表 */}
      {logos.length > 0 ? (
        <div className="space-y-2">
          {logos.map((logo, index) => (
            <div key={index} className="flex items-center gap-3 rounded-xl border border-hairline bg-surface-0 p-3">
              <div className="size-10 shrink-0 overflow-hidden rounded-md bg-neutral-900">
                {logo.publicUrl ? <img src={resolveUrl(logo.publicUrl) ?? ''} alt={logo.fileName ?? ''} className="size-full object-contain" /> : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-neutral-200">{logo.fileName ?? 'logo'}</div>
                <div className="mt-1 flex gap-2">
                  <select value={logo.position ?? 'bottom-right'} onChange={(e) => onUpdateLogo(index, { position: e.target.value, x: null, y: null })} className="h-6 rounded border border-hairline bg-surface-0 px-1 text-[10px] text-neutral-300 focus:outline-none">
                    <option value="top-left">左上</option><option value="top-right">右上</option><option value="bottom-left">左下</option><option value="bottom-right">右下</option><option value="center">居中</option>
                  </select>
                  <select value={logo.scale ?? 'medium'} onChange={(e) => onUpdateLogo(index, { scale: e.target.value })} className="h-6 rounded border border-hairline bg-surface-0 px-1 text-[10px] text-neutral-300 focus:outline-none">
                    <option value="small">小</option><option value="medium">中</option><option value="large">大</option>
                  </select>
                </div>
              </div>
              <button type="button" onClick={() => onRemoveLogo(index)} className="rounded p-1 text-neutral-500 hover:bg-neutral-800 hover:text-red-400">
                <Trash2 className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-hairline bg-surface-0 text-center">
          <div className="rounded-xl bg-neutral-800 p-3 text-neutral-400">
            <Palette className="size-6" strokeWidth={1.5} />
          </div>
          <div className="text-sm font-medium text-neutral-300">还没有logo</div>
          <div className="max-w-xs text-xs text-neutral-500">PNG / JPG / WEBP · 建议透明背景 PNG</div>
          <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="mt-1 inline-flex h-8 items-center gap-1 rounded-full border border-hairline bg-neutral-800 px-4 text-xs font-medium text-neutral-100 transition-colors hover:bg-neutral-700 disabled:opacity-50">
            {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
            {uploading ? '上传中…' : '上传logo'}
          </button>
        </div>
      )}

      {logos.length > 0 ? (
        <div className="rounded-xl border border-hairline bg-surface-0 px-4 py-3 text-xs text-neutral-400">
          提示：在右侧预览图上直接拖动logo调整位置，拖动结果会自动保存到模板配置。
        </div>
      ) : null}
    </div>
  )
}

function BackgroundPanel({ background, onUpload, onRemove, enabled, onToggleEnabled }: {
  background: { assetId?: number; publicUrl?: string; fileName?: string } | null
  onUpload: (file: { assetId: number; publicUrl: string; fileName: string }) => void
  onRemove: () => void
  enabled: boolean
  onToggleEnabled: (enabled: boolean) => void
}) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function handleFile(file: File) {
    setUploading(true); setError(null)
    try {
      const res = await uploadFile<ApiResponse<AssetUploadResult>>('/api/assets/upload', file)
      onUpload({ assetId: res.data.id, publicUrl: res.data.publicUrl, fileName: res.data.fileName })
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败')
    } finally { setUploading(false) }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between border-b border-hairline pb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-neutral-800 p-2 text-neutral-200">
            <Frame className="size-4" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-sm font-semibold text-neutral-100">背景</div>
            <div className="text-xs text-neutral-400">上传背景图，作为生成视频的底图氛围。</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={enabled} onCheckedChange={onToggleEnabled} />
          <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = '' }} />
          <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="inline-flex h-8 items-center gap-1 rounded-full border border-hairline bg-neutral-800 px-3 text-xs font-medium text-neutral-100 transition-colors hover:bg-neutral-700 disabled:opacity-50">
            {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
            {uploading ? '上传中…' : '上传'}
          </button>
        </div>
      </div>

      {error ? <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-2 text-xs text-red-400">{error}</div> : null}

      {background ? (
        <div className="flex items-center gap-3 rounded-xl border border-hairline bg-surface-0 p-3">
          <div className="size-12 shrink-0 overflow-hidden rounded-md bg-neutral-900">
            {background.publicUrl ? <img src={resolveUrl(background.publicUrl) ?? ''} alt={background.fileName ?? ''} className="size-full object-cover" /> : null}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium text-neutral-200">{background.fileName ?? '背景图'}</div>
            <div className="text-[11px] text-neutral-500">PNG / JPG / WEBP</div>
          </div>
          <button type="button" onClick={onRemove} className="rounded p-1 text-neutral-500 hover:bg-neutral-800 hover:text-red-400"><Trash2 className="size-3.5" /></button>
        </div>
      ) : (
        <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-hairline bg-surface-0 text-center">
          <div className="rounded-xl bg-neutral-800 p-3 text-neutral-400">
            <Frame className="size-6" strokeWidth={1.5} />
          </div>
          <div className="text-sm font-medium text-neutral-300">还没有背景图</div>
          <div className="max-w-xs text-xs text-neutral-500">PNG / JPG / WEBP</div>
          <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="mt-1 inline-flex h-8 items-center gap-1 rounded-full border border-hairline bg-neutral-800 px-4 text-xs font-medium text-neutral-100 transition-colors hover:bg-neutral-700 disabled:opacity-50">
            {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
            {uploading ? '上传中…' : '上传背景图'}
          </button>
        </div>
      )}
    </div>
  )
}

function SettingsPanel({ detail, localConfig, onUpdate }: {
  detail: Template
  localConfig: Record<string, unknown>
  onUpdate: (updates: Record<string, unknown>) => void
}) {
  const language = (localConfig.language as string) ?? 'en'
  const translateTo = (localConfig.translateTo as string) ?? 'none'
  const script = (localConfig.script as string) ?? 'native'
  const orientation = (localConfig.aspectRatio as string) ?? detail.aspectRatio ?? '9:16'
  const resolution = (localConfig.resolution as string) ?? `${detail.width ?? 1080}x${detail.height ?? 1920}`

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3 border-b border-hairline pb-3">
        <div className="rounded-lg bg-neutral-800 p-2 text-neutral-200">
          <Settings className="size-4" strokeWidth={1.8} />
        </div>
        <div>
          <div className="text-sm font-semibold text-neutral-100">设置</div>
          <div className="text-xs text-neutral-400">模板语言、比例和输出规格。</div>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        {/* Language */}
        <div>
          <div className="mb-3 text-sm font-medium leading-tight text-neutral-200">语言</div>
          <select
            value={language}
            onChange={(e) => onUpdate({ language: e.target.value })}
            className="h-10 w-full rounded-lg border border-hairline bg-[#181818] px-4 text-sm text-neutral-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          >
            <option value="en">英语</option>
            <option value="zh">中文</option>
            <option value="ja">日本語</option>
            <option value="es">Español</option>
            <option value="ko">한국어</option>
            <option value="de">Deutsch</option>
            <option value="fr">Français</option>
            <option value="pt">Português</option>
          </select>
        </div>

        {/* Translate to */}
        <div>
          <div className="mb-3 text-sm font-medium leading-tight text-neutral-200">翻译为</div>
          <select
            value={translateTo}
            onChange={(e) => onUpdate({ translateTo: e.target.value })}
            className="h-10 w-full rounded-lg border border-hairline bg-[#181818] px-4 text-sm text-neutral-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          >
            <option value="none">不翻译</option>
            <option value="en">英语</option>
            <option value="zh">中文</option>
            <option value="ja">日本語</option>
            <option value="es">Español</option>
            <option value="ko">한국어</option>
            <option value="de">Deutsch</option>
            <option value="fr">Français</option>
          </select>
        </div>

        {/* Script */}
        <div>
          <div className="mb-3 text-sm font-medium leading-tight text-neutral-200">文字系统</div>
          <select
            value={script}
            onChange={(e) => onUpdate({ script: e.target.value })}
            className="h-10 w-full rounded-lg border border-hairline bg-[#181818] px-4 text-sm text-neutral-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          >
            <option value="native">原生</option>
            <option value="latin">拉丁</option>
            <option value="cyrillic">西里尔</option>
            <option value="arabic">阿拉伯</option>
            <option value="cjk">中日韩</option>
          </select>
        </div>

        {/* Orientation */}
        <div>
          <div className="mb-3 text-sm font-medium leading-tight text-neutral-200">方向</div>
          <select
            value={orientation}
            onChange={(e) => {
              const val = e.target.value
              const res = val === '1:1' ? '1080x1080' : val === '16:9' ? '1920x1080' : '1080x1920'
              onUpdate({ aspectRatio: val, resolution: res })
            }}
            className="h-10 w-full rounded-lg border border-hairline bg-[#181818] px-4 text-sm text-neutral-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          >
            <option value="9:16">竖版（9:16）</option>
            <option value="1:1">方形（1:1）</option>
            <option value="16:9">横版（16:9）</option>
          </select>
        </div>

        {/* Resolution */}
        <div>
          <div className="mb-3 text-sm font-medium leading-tight text-neutral-200">分辨率</div>
          <select
            value={resolution}
            onChange={(e) => onUpdate({ resolution: e.target.value })}
            className="h-10 w-full rounded-lg border border-hairline bg-[#181818] px-4 text-sm text-neutral-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          >
            <option value="1080x1920">1080p</option>
            <option value="720x1280">720p</option>
            <option value="2160x3840">4K</option>
          </select>
        </div>
      </div>
    </div>
  )
}

function FontsPanel({ uploadedFonts, onAddFont, onRemoveFont, onSelectFont, canUpload, onSubscriptionRequired }: {
  uploadedFonts: Array<{ assetId?: number; publicUrl?: string; fileName?: string; fontFamily?: string }>
  onAddFont: (font: { assetId: number; publicUrl: string; fileName: string; fontFamily: string }) => void
  onRemoveFont: (index: number) => void
  onSelectFont: (family: string) => void
  canUpload: boolean
  onSubscriptionRequired: () => void
}) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function handleFile(file: File) {
    if (!canUpload) {
      onSubscriptionRequired()
      return
    }
    setUploading(true); setError(null)
    try {
      const res = await uploadFile<ApiResponse<AssetUploadResult>>('/api/assets/upload?purpose=brand_template', file)
      const fontFamily = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')
      onAddFont({ assetId: res.data.id, publicUrl: res.data.publicUrl, fileName: res.data.fileName, fontFamily })
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败')
    } finally { setUploading(false) }
  }

  const requestUpload = () => {
    if (!canUpload) {
      onSubscriptionRequired()
      return
    }
    fileInputRef.current?.click()
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Header: Saved Fonts + Upload */}
      <div className="flex items-center justify-between border-b border-hairline pb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-neutral-800 p-2 text-neutral-200">
            <Languages className="size-4" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-sm font-semibold text-neutral-100">已保存字体</div>
            <div className="text-xs text-neutral-400">上传自定义字体，在字幕和文字样式中可用。</div>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept=".ttf,.otf,.woff,.woff2" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = '' }} />
        <button type="button" disabled={uploading} onClick={requestUpload} className="inline-flex h-8 items-center gap-1 rounded-full border border-hairline bg-neutral-800 px-3 text-xs font-medium text-neutral-100 transition-colors hover:bg-neutral-700 disabled:opacity-50">
          {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
          {uploading ? '上传中…' : '上传'}
        </button>
      </div>

      {error ? <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-2 text-xs text-red-400">{error}</div> : null}

      {/* 已上传字体列表 */}
      {uploadedFonts.length > 0 ? (
        <div className="space-y-2">
          {uploadedFonts.map((font, index) => (
            <div key={index} className="flex items-center gap-3 rounded-xl border border-hairline bg-neutral-900 p-3">
              {/* 字体预览 */}
              <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-neutral-800">
                <span
                  className="text-xl font-bold text-neutral-100"
                  style={{
                    fontFamily: font.publicUrl
                      ? `"${font.fontFamily ?? font.fileName ?? 'font'}", sans-serif`
                      : undefined,
                  }}
                >
                  Aa
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-neutral-100">{font.fontFamily ?? font.fileName ?? '字体'}</div>
                <div className="truncate text-[11px] text-neutral-500">{font.fileName ?? ''}</div>
              </div>
              <button type="button" onClick={() => onRemoveFont(index)} className="rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-white/6 hover:text-red-400">
                <Trash2 className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        /* 空状态（对齐 reap） */
        <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 text-center">
          <div className="flex size-10 items-center justify-center rounded-full bg-neutral-700">
            <Upload className="size-4 text-neutral-200" />
          </div>
          <div className="text-sm font-medium text-neutral-200">还没有字体</div>
          <div className="text-xs text-neutral-500">上传第一个字体后即可开始使用</div>
          <button type="button" disabled={uploading} onClick={requestUpload} className="mt-1 inline-flex h-8 items-center gap-1 rounded-full border border-hairline bg-neutral-800 px-4 text-xs font-medium text-neutral-100 transition-colors hover:bg-neutral-700 disabled:opacity-50">
            {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
            {uploading ? '上传中…' : '上传字体'}
          </button>
        </div>
      )}

      {/* 内置字体库 - 点击设为当前字幕字体 */}
      <div className="border-t border-hairline pt-4">
        <div className="mb-3 text-xs font-medium text-neutral-400">内置字体库</div>
        <div className="grid gap-2 md:grid-cols-2">
          {FONT_LIBRARY.map((font, index) => (
            <button
              key={font.family}
              type="button"
              onClick={() => onSelectFont(font.family)}
              className={cn(
                'rounded-xl border bg-neutral-900 p-3 text-left transition-colors hover:border-neutral-600',
                index === 0 ? 'border-sky-600/40' : 'border-hairline',
              )}
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="truncate text-xs font-semibold text-neutral-200">{font.family}</span>
                <span className="shrink-0 rounded-full border border-hairline px-1.5 py-0.5 text-[9px] text-neutral-500">{font.useFor}</span>
              </div>
              <div className="rounded-lg border border-hairline bg-neutral-800/60 px-3 py-3">
                <div
                  className="text-lg font-semibold leading-tight text-neutral-100"
                  style={{ fontFamily: resolveTextEditorFontStack(font.family) }}
                >
                  {font.sample}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function PlaceholderPanel({ activeTab }: { activeTab: BrandingTabKey }) {
  const label = BRANDING_TABS.find((tab) => tab.key === activeTab)?.label ?? activeTab

  return (
    <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-hairline bg-surface-0 text-center">
      <LayoutTemplate className="size-6 text-neutral-400" />
      <div className="text-sm font-medium text-neutral-200">{label}</div>
      <div className="max-w-sm text-sm text-neutral-400">
        这里已为新布局预留独立配置区，后续可以继续接入真实的模块数据和编辑能力。
      </div>
    </div>
  )
}

function MetricCard({
  label,
  value,
  note,
}: {
  label: string
  value: string
  note: string
}) {
  return (
    <div className="rounded-2xl border border-hairline bg-surface-0 p-4">
      <div className="text-[11px] uppercase tracking-wide text-neutral-400">{label}</div>
      <div className="mt-2 text-lg font-semibold text-neutral-100">{value}</div>
      <div className="mt-1 text-xs text-neutral-400">{note}</div>
    </div>
  )
}

function EditorSection({
  title,
  trailing,
  children,
}: {
  title: string
  trailing?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="border-b border-hairline pb-5 last:border-b-0">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm font-medium text-neutral-200">{title}</div>
        {trailing}
      </div>
      {children}
    </section>
  )
}

/** 字重选择器 */
function FontWeightSelect({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const options = [
    { value: 400, label: '常规' },
    { value: 500, label: '中等' },
    { value: 600, label: '半粗体' },
    { value: 700, label: '粗体' },
    { value: 800, label: '特粗' },
    { value: 900, label: '黑体' },
  ]
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-10 w-full rounded-xl border border-hairline bg-[#181818] px-3 text-sm text-neutral-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label} {opt.value}</option>
      ))}
    </select>
  )
}

function MiniPill({
  children,
  active = false,
  onClick,
}: {
  children: React.ReactNode
  active?: boolean
  onClick?: () => void
}) {
  const cls = cn(
    'inline-flex min-h-8 cursor-pointer items-center rounded-full border px-3 text-xs font-medium transition-colors',
    active
      ? 'border-sky-500/40 bg-sky-500/10 text-sky-300'
      : 'border-hairline bg-surface-0 text-neutral-400 hover:border-neutral-600 hover:text-neutral-200',
  )
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cls}>
        {children}
      </button>
    )
  }
  return <span className={cn(cls, 'cursor-default')}>{children}</span>
}

/** 可编辑的颜色选择器（color picker + text input） */
function EditableColorField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  const safeValue = value.startsWith('#') ? value.slice(0, 7) : '#ffffff'
  return (
    <div>
      <div className="mb-2 text-sm font-medium text-neutral-200">{label}</div>
      <div className="flex h-10 items-center gap-2 rounded-xl border border-hairline bg-[#181818] px-3">
        <input
          type="color"
          value={safeValue}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="size-6 cursor-pointer rounded border-0 bg-transparent p-0"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-transparent text-sm text-neutral-200 focus:outline-none"
        />
      </div>
    </div>
  )
}

function RangeRow({
  value,
  min = 0,
  max = 10,
  step = 1,
  onChange,
}: {
  value: number
  min?: number
  max?: number
  step?: number
  onChange?: (v: number) => void
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="text-neutral-400">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange?.(Number(e.target.value))}
        className="w-full accent-sky-500"
      />
    </div>
  )
}

function TemplatePreviewPlaceholder({ template }: { template: Template }) {
  const isPortrait = template.aspectRatio === '9:16'
  const isSquare = template.aspectRatio === '1:1'

  return (
    <div className="flex size-full items-center justify-center bg-gradient-to-br from-muted to-background">
      <div
        className={cn(
          'rounded border border-hairline bg-surface-1/50',
          isPortrait ? 'h-[60px] w-[34px]' : isSquare ? 'size-[60px]' : 'h-[34px] w-[60px]',
        )}
      />
    </div>
  )
}

function AspectGlyph({ aspectRatio }: { aspectRatio: string | null }) {
  const isPortrait = aspectRatio === '9:16'
  const isSquare = aspectRatio === '1:1'

  return (
    <div
      className={cn(
        'rounded-sm border border-white/70',
        isPortrait ? 'h-4 w-2.5' : isSquare ? 'size-3.5' : 'h-2.5 w-4',
      )}
    />
  )
}

function AudiogramGlyph({ type }: { type: string }) {
  if (type === 'radial') {
    return (
      <div className="group/glyph relative size-16 rounded-full">
        {Array.from({ length: 18 }).map((_, index) => (
          <span
            key={index}
            className="absolute left-1/2 top-1/2 h-5 w-px origin-bottom bg-white/80 transition-all duration-200 group-hover/glyph:animate-pulse"
            style={{
              transform: `translate(-50%, -100%) rotate(${index * 20}deg)`,
              animationDelay: `${index * 50}ms`,
            }}
          />
        ))}
      </div>
    )
  }

  if (type === 'wave') {
    return (
      <div className="group/glyph">
        <svg width="120" height="30" viewBox="0 0 120 30" className="text-white/80 transition-all duration-300 group-hover/glyph:scale-110">
          <path
            d="M0 15 C10 15 10 7 20 7 S30 23 40 23 S50 9 60 9 S70 21 80 21 S90 12 100 12 S110 18 120 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      </div>
    )
  }

  return (
    <div className="group/glyph flex h-10 items-end gap-1">
      {Array.from({ length: 18 }).map((_, index) => {
        const heights =
          type === 'bars'
            ? [18, 20, 22, 24, 26, 28, 28, 26, 24, 22, 20, 18, 16, 14, 12, 10, 8, 6]
            : type === 'hills'
              ? [4, 7, 10, 14, 18, 22, 26, 29, 30, 29, 26, 22, 18, 14, 10, 7, 5, 4]
              : [3, 4, 5, 7, 9, 12, 16, 20, 24, 20, 16, 12, 9, 7, 5, 4, 3, 3]

        return (
          <span
            key={index}
            className={cn(
              'block w-1 rounded-full bg-white/85 transition-all duration-300',
              type === 'hills' ? 'rounded-[999px]' : '',
            )}
            style={{
              height: `${heights[index]!}px`,
              animationDelay: `${index * 40}ms`,
            }}
          />
        )
      })}
      {/* hover 时条形跳动动画 */}
      <style>{`
        .group\\/glyph:hover span {
          animation: barBounce 0.6s ease-in-out infinite alternate;
        }
        @keyframes barBounce {
          0% { transform: scaleY(0.4); }
          100% { transform: scaleY(1.2); }
        }
      `}</style>
    </div>
  )
}

function buildVariantSummary(
  localConfig: Record<string, unknown>,
  detail: Template | null,
  activeTab: BrandingTabKey,
) {
  // 字幕样式由 selectedStyleKey 派生，再被 localConfig.captions 的覆写字段覆盖
  const styleKey = (localConfig.captionStyleKey as string) ?? 'blue'
  const base = getCaptionStyleConfig(styleKey)
  const captions = (localConfig.captions as Record<string, unknown> | undefined) ?? {}
  const renderConfig = buildCaptionRenderConfig(base, captions)

  return [
    {
      label: '标签',
      value: BRANDING_TABS.find((item) => item.key === activeTab)?.label ?? activeTab,
    },
    {
      label: '字体',
      value: renderConfig.fontFamily,
    },
    {
      label: '颜色',
      value: renderConfig.color,
    },
    {
      label: '高亮',
      value: renderConfig.highlightColor ?? renderConfig.backgroundColor ?? '黄色',
    },
    {
      label: '方向',
      value: orientationLabel(detail?.aspectRatio ?? null),
    },
    {
      label: '分辨率',
      value: resolutionLabel(detail),
    },
  ]
}

function buildBrandingVariantSummary(
  localConfig: Record<string, unknown>,
  detail: Template | null,
  activeTab: BrandingTabKey,
) {
  if (activeTab === 'text') {
    const textConfig = (localConfig.text as Record<string, unknown> | undefined) ?? {}
    return [
      {
        label: '标签',
        value: BRANDING_TABS.find((item) => item.key === activeTab)?.label ?? activeTab,
      },
      {
        label: '字体',
        value: getConfigString(textConfig, ['fontFamily'], 'Inter'),
      },
      {
        label: '颜色',
        value: getConfigString(textConfig, ['color'], '#FFFFFF'),
      },
      {
        label: '高亮',
        value: getConfigString(textConfig, ['accentColor'], '#FFD93D'),
      },
      {
        label: '方向',
        value: orientationLabel(detail?.aspectRatio ?? null),
      },
      {
        label: '分辨率',
        value: resolutionLabel(detail),
      },
    ]
  }

  return buildVariantSummary(localConfig, detail, activeTab)
}

function getConfigString(
  config: Record<string, unknown> | null | undefined,
  keys: string[],
  fallback: string,
) {
  if (!config) return fallback
  for (const key of keys) {
    const value = config[key]
    if (typeof value === 'string' && value.trim()) return value
  }
  return fallback
}

function getConfigNumber(
  config: Record<string, unknown> | null | undefined,
  keys: string[],
  fallback: number,
) {
  if (!config) return fallback
  for (const key of keys) {
    const value = config[key]
    if (typeof value === 'number') return value
  }
  return fallback
}

function getConfigBool(
  config: Record<string, unknown> | null | undefined,
  keys: string[],
  fallback: boolean,
) {
  if (!config) return fallback
  for (const key of keys) {
    const value = config[key]
    if (typeof value === 'boolean') return value
  }
  return fallback
}

function orientationLabel(aspectRatio: string | null) {
  if (aspectRatio === '9:16') return '竖版（9:16）'
  if (aspectRatio === '16:9') return '横版（16:9）'
  if (aspectRatio === '1:1') return '方形（1:1）'
  return aspectRatio ?? '竖版（9:16）'
}

function resolutionLabel(detail: Template | null) {
  if (!detail?.width || !detail?.height) return '-'
  return `${detail.width}x${detail.height}`
}
